import '../sheet.css';

import { applyCellDisplay, applyCellInlineStyleRecord, getCellEditor } from './cell-dom.ts';
import { canonicalCssColor, formatSelectOptionMarkup, linkButtonLabel } from './cell-display.ts';
import {
  cellPlainTextForSearch,
  columnValueType,
  filterSelectOptions,
  isLinkColumn,
  isSelectColumn,
  parseCommittedCellValue,
  resolveSelectLabel,
} from './cell-value.ts';
import { cellAddress, cellRangeLabel } from './cell-address.ts';
import {
  SHEET_CELL_PAD_X_PX,
  SHEET_SELECT_CARET_INSET_PX,
  blocksSpill,
  spillContentWidth,
  spillDirectionFor,
  spillRoom,
  spills as spillWidensBox,
  type SpillSlot,
} from './cell-overflow.ts';
import { createCellInputBar, type CellInputBar, type ValueBarMove } from './cell-input-bar.ts';
import {
  escapeTsvField,
  getInternalClipboardPlain,
  parseClipboardRows,
  parseHtmlClipboardTable,
  setInternalClipboardPlain,
} from './clipboard.ts';
import {
  clampColumnWidth,
  persistColumnWidths,
  readPersistedColumnWidths,
} from './column-width.ts';
import {
  clampToVisibleColumn,
  firstVisibleColumnIndex,
  isHiddenColumn,
  isVisibleColumn,
  lastVisibleColumnIndex,
  stepVisibleColumn,
  sumVisibleColumnWidths,
  visibleColumnCount,
} from './column-visibility.ts';
import {
  cellSpansEqual,
  cloneCellSpans,
  coveredCellKeys,
  expandRectToSpans,
  mergeSpanList,
  spanCovering,
  spansAfterClearingRow,
  spansOverlap,
  unmergeSpanList,
} from './cell-span.ts';
import {
  cloneFloatingImages,
  floatingImagesEqual,
  mergeFloatingImages,
  normalizeFloatingImages,
} from './floating-image.ts';
import { mountFloatingImageLayer, type FloatingImageLayer } from './floating-image-layer.ts';
import { cellKey } from './data-store.ts';
import { cellWantsWrap } from './cell-style.ts';
import { cellMapsEqual, createSpreadsheetHistory, type HistoryCellMap } from './history.ts';
import { jumpToEdge } from './navigation.ts';
import { createPersistErrorController } from './persist-error.ts';
import { createRowContextMenu } from './row-context-menu.ts';
import type {
  CellSpan,
  FloatingImage,
  CollabPresenceMode,
  CollabPresencePayload,
  RemoteCollabPeer,
  SpreadsheetCellStyleDeclarations,
  SpreadsheetColumn,
  SpreadsheetConfig,
  SpreadsheetCellInit,
  SpreadsheetMountHandle,
  SpreadsheetSelectOption,
} from './types.ts';
import { resolveEnabledUiCapabilities } from './types.ts';

/** Mount a configurable spreadsheet. Tear down by removing `container` children if remounting. */
export function mountSpreadsheet(
  container: HTMLElement,
  config: SpreadsheetConfig,
): SpreadsheetMountHandle {
  const columns = config.columns;
  const dataColumnCount = columns.length;
  const dataRowCount = config.rowCount;
  const ghostRowCount = Math.max(
    0,
    Math.min(
      10_000,
      Math.floor(
        typeof config.ghostRowCount === 'number' && Number.isFinite(config.ghostRowCount)
          ? config.ghostRowCount
          : 0,
      ),
    ),
  );
  const mountedRowCount = dataRowCount + ghostRowCount;
  function isGhostRow(row: number): boolean {
    return row > dataRowCount;
  }
  const defaultRowHeight = config.defaultRowHeightPx ?? 28;
  const rowHeights = Array.from({ length: mountedRowCount }, () => defaultRowHeight);
  /** `'spill'` (default): long text paints across empty columns to its right. */
  const cellOverflowMode: 'spill' | 'clip' = config.cellOverflow === 'clip' ? 'clip' : 'spill';
  const columnWidthPersistenceKey = config.columnWidthPersistenceKey;
  const columnWidths = columns.map((c) => clampColumnWidth(c.widthPx));
  const persistedWidths = readPersistedColumnWidths(columnWidthPersistenceKey, columns.length);
  if (persistedWidths) {
    for (let i = 0; i < columnWidths.length; i++) {
      columnWidths[i] = persistedWidths[i]!;
    }
  }
  let dataSheetTotalWidth = sumVisibleColumnWidths(columns, columnWidths);

  const ghostColumnsEnabled =
    dataColumnCount > 0 &&
    config.enableGhostColumns !== false &&
    typeof ResizeObserver === 'function';
  const ghostColumnWidthPx = Math.round(
    Math.min(400, Math.max(24, config.ghostColumnWidthPx ?? 72)),
  );
  const maxGhostColumns = Math.min(
    500,
    Math.max(0, Math.floor(config.maxGhostColumns ?? 80)),
  );

  let ghostColumnCount = 0;
  let cumulativeColumnWidths: number[] = [];
  let mountedSheetCellsWidth = dataSheetTotalWidth;

  function widthOfMountedCol1Based(i: number): number {
    if (i <= dataColumnCount) {
      if (isHiddenColumn(columns, i)) return 0;
      return columnWidths[i - 1]!;
    }
    return ghostColumnWidthPx;
  }

  function rebuildCumulativeColumnWidthsAndMountedWidth(): void {
    const m = dataColumnCount + ghostColumnCount;
    cumulativeColumnWidths = Array.from({ length: m }, () => 0);
    for (let i = 1; i < m; i++) {
      cumulativeColumnWidths[i] = cumulativeColumnWidths[i - 1]! + widthOfMountedCol1Based(i);
    }
    let t = 0;
    for (let i = 1; i <= m; i++) t += widthOfMountedCol1Based(i);
    mountedSheetCellsWidth = t;
  }

  rebuildCumulativeColumnWidthsAndMountedWidth();

  const cumulativeRowHeights: number[] = Array.from({ length: mountedRowCount }, () => 0);
  for (let i = 1; i < mountedRowCount; i++) {
    cumulativeRowHeights[i] = cumulativeRowHeights[i - 1]! + rowHeights[i - 1]!;
  }
  const sheetTotalHeight = rowHeights.reduce((a, b) => a + b, 0);
  /** Fixed gutter for row indices; added to horizontal extent and cell `left` offsets. */
  const rowHeaderWidthPx = 48;

  const data = config.data;
  const { growRowCountForPaste, suppressOutboundSyncDuring, onRowDeleted } = config;

  const historyEnabled =
    typeof data.replaceCell === 'function' &&
    typeof data.hasCell === 'function' &&
    typeof data.getStoredCell === 'function';

  const history = historyEnabled ? createSpreadsheetHistory() : null;

  const commentsUiEnabled = resolveEnabledUiCapabilities(config.enabledUiCapabilities).has(
    'comment',
  );
  const commentsEnabled = commentsUiEnabled && historyEnabled;

  function keyToRowCol(k: string): { row: number; col: number } {
    const [rs, cs] = k.split(':');
    return { row: Number(rs), col: Number(cs) };
  }

  function currentSpans(): readonly CellSpan[] {
    return data.getCellSpans?.() ?? [];
  }

  function coveringSpan(row: number, col: number): CellSpan | null {
    return spanCovering(currentSpans(), row, col);
  }

  function isCoveredCell(row: number, col: number): boolean {
    const span = coveringSpan(row, col);
    return span !== null && (span.row !== row || span.col !== col);
  }

  function captureSnapshot(keys: readonly string[]): HistoryCellMap {
    const m: HistoryCellMap = new Map();
    for (const k of keys) {
      const { row, col } = keyToRowCol(k);
      if (data.hasCell?.(row, col)) {
        m.set(k, data.getStoredCell?.(row, col));
      } else {
        m.set(k, undefined);
      }
    }
    return m;
  }

  function applySnapshot(snapshot: HistoryCellMap): void {
    if (!data.replaceCell) return;
    const run = (): void => {
      for (const [k, state] of snapshot) {
        const { row, col } = keyToRowCol(k);
        data.replaceCell!(row, col, state === undefined ? null : state);
      }
    };
    if (config.suppressOutboundSyncDuring) {
      config.suppressOutboundSyncDuring(run);
    } else {
      run();
    }
  }

  function hydrateCellFromStore(row: number, col: number): void {
    if (isGhostRow(row)) return;
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    const colDef = columnAt(col);
    const raw = data.get(row, col);
    const display = raw !== undefined ? String(raw) : '';
    const content = el.querySelector<HTMLDivElement>('.sheet-cell-content');
    if (content) applyCellDisplay(content, colDef, display);
    syncCellChrome(row, col);
    const inp = getCellEditor(el);
    if (inp) inp.value = raw !== undefined ? String(raw) : '';
  }

  function flashRemoteMarker(row: number, col: number, hue: number): void {
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    const h = ((hue % 360) + 360) % 360;
    el.style.setProperty('--collab-flash-hue', String(h));
    el.classList.add('sheet-cell--collab-remote-flash');
    window.setTimeout(() => {
      el.classList.remove('sheet-cell--collab-remote-flash');
      el.style.removeProperty('--collab-flash-hue');
    }, 800);
  }

  function applyExternalValue(
    row: number,
    col: number,
    value: string | number,
    options?: { remoteMarkerHue?: number },
  ): boolean {
    if (row < 1 || row > dataRowCount || col < 1 || col > dataColumnCount) return false;
    if (isHiddenCol(col)) {
      data.set(row, col, value);
      return true;
    }
    if (!cells.has(cellKey(row, col))) return false;
    data.set(row, col, value);
    hydrateCellFromStore(row, col);
    persistError.clear(row, col);
    if (options?.remoteMarkerHue !== undefined) {
      flashRemoteMarker(row, col, options.remoteMarkerHue);
    }
    return true;
  }

  function applyExternalStyle(
    row: number,
    col: number,
    style: SpreadsheetCellStyleDeclarations | null,
    options?: { remoteMarkerHue?: number },
  ): boolean {
    if (row < 1 || row > dataRowCount || col < 1 || col > dataColumnCount) return false;
    const next = style && Object.keys(style).length > 0 ? style : undefined;
    if (!data.setCellStyle) return false;
    if (isHiddenCol(col)) {
      data.setCellStyle(row, col, next);
      return true;
    }
    if (!cells.has(cellKey(row, col))) return false;
    data.setCellStyle(row, col, next);
    syncCellChrome(row, col);
    if (options?.remoteMarkerHue !== undefined) {
      flashRemoteMarker(row, col, options.remoteMarkerHue);
    }
    return true;
  }

  function hydrateFromHistoryRecord(before: HistoryCellMap, after: HistoryCellMap): void {
    const keys = new Set<string>([...before.keys(), ...after.keys()]);
    for (const k of keys) {
      const { row, col } = keyToRowCol(k);
      hydrateCellFromStore(row, col);
    }
  }

  function pushHistoryIfChanged(before: HistoryCellMap, after: HistoryCellMap): void {
    if (!history) return;
    if (cellMapsEqual(before, after)) return;
    history.pushRecord(before, after);
  }

  const selectArea: {
    active: boolean;
    row: number;
    col: number;
    rowEnd: number;
    colEnd: number;
  } = {
    active: false,
    row: 1,
    col: 1,
    rowEnd: 1,
    colEnd: 1,
  };

  type CopyMarqueeBounds = {
    minRow: number;
    maxRow: number;
    minCol: number;
    maxCol: number;
  };
  let copyMarquee: CopyMarqueeBounds | null = null;
  let copyMarqueeEl: HTMLDivElement | null = null;
  /** Solid border drawn around the whole active multi-cell selection. */
  let selectionOutlineEl: HTMLDivElement | null = null;
  /** Visually-hidden polite live region announcing the active cell for screen readers. */
  let srAnnounceEl: HTMLDivElement | null = null;

  function syncCopyMarqueeLayout(): void {
    if (!copyMarqueeEl || !copyMarquee) {
      if (copyMarqueeEl) copyMarqueeEl.hidden = true;
      return;
    }
    const { minRow, maxRow, minCol, maxCol } = copyMarquee;
    const top = cumulativeRowHeights[minRow - 1]!;
    const left = rowHeaderWidthPx + cumulativeColumnWidths[minCol - 1]!;
    const right =
      rowHeaderWidthPx + cumulativeColumnWidths[maxCol - 1]! + columnWidths[maxCol - 1]!;
    const bottom = cumulativeRowHeights[maxRow - 1]! + rowHeights[maxRow - 1]!;
    copyMarqueeEl.style.top = `${top}px`;
    copyMarqueeEl.style.left = `${left}px`;
    copyMarqueeEl.style.width = `${Math.max(0, right - left)}px`;
    copyMarqueeEl.style.height = `${Math.max(0, bottom - top)}px`;
    copyMarqueeEl.hidden = false;
  }

  function syncSelectionOutlineLayout(): void {
    if (!selectionOutlineEl) return;
    if (!selectArea.active) {
      selectionOutlineEl.hidden = true;
      return;
    }
    const minRow = Math.min(selectArea.row, selectArea.rowEnd);
    const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
    const minCol = Math.min(selectArea.col, selectArea.colEnd);
    const maxCol = Math.max(selectArea.col, selectArea.colEnd);
    // When a copy marquee covers the exact same range, let its marching-ants
    // animation show through instead of painting a static border over it.
    if (
      copyMarquee &&
      copyMarquee.minRow === minRow &&
      copyMarquee.maxRow === maxRow &&
      copyMarquee.minCol === minCol &&
      copyMarquee.maxCol === maxCol
    ) {
      selectionOutlineEl.hidden = true;
      return;
    }
    const top = cumulativeRowHeights[minRow - 1]!;
    const left = rowHeaderWidthPx + cumulativeColumnWidths[minCol - 1]!;
    const right =
      rowHeaderWidthPx + cumulativeColumnWidths[maxCol - 1]! + columnWidths[maxCol - 1]!;
    const bottom = cumulativeRowHeights[maxRow - 1]! + rowHeights[maxRow - 1]!;
    selectionOutlineEl.style.top = `${top}px`;
    selectionOutlineEl.style.left = `${left}px`;
    selectionOutlineEl.style.width = `${Math.max(0, right - left)}px`;
    selectionOutlineEl.style.height = `${Math.max(0, bottom - top)}px`;
    selectionOutlineEl.hidden = false;
  }

  function setCopyMarquee(bounds: CopyMarqueeBounds): void {
    copyMarquee = bounds;
    syncCopyMarqueeLayout();
    syncSelectionOutlineLayout();
  }

  function setCopyMarqueeFromSelection(): void {
    if (selectArea.active) {
      setCopyMarquee({
        minRow: Math.min(selectArea.row, selectArea.rowEnd),
        maxRow: Math.max(selectArea.row, selectArea.rowEnd),
        minCol: Math.min(selectArea.col, selectArea.colEnd),
        maxCol: Math.max(selectArea.col, selectArea.colEnd),
      });
      return;
    }
    setCopyMarquee({
      minRow: active.row,
      maxRow: active.row,
      minCol: active.col,
      maxCol: active.col,
    });
  }

  function clearCopyMarquee(): void {
    copyMarquee = null;
    if (copyMarqueeEl) copyMarqueeEl.hidden = true;
    syncSelectionOutlineLayout();
  }

  const cells = new Map<string, HTMLDivElement>();
  let active = { row: 1, col: 1 };
  /**
   * Excel/Sheets-style navigation-vs-edit toggle. Navigation mode (false) is
   * the default after movement: the active cell renders its display content,
   * keyboard focus stays on the viewport, and copy/paste/range gestures behave
   * as if no editor were open. Edit mode (true) is entered via F2,
   * double-click, or typing a printable key — the active cell swaps to its
   * `<input>` (via the `sheet-cell-editing` class), gets focus, and edits the
   * text buffer until Escape/Tab/Enter commit or cancel.
   */
  let editMode = false;
  /**
   * When the user moves with Tab/Shift+Tab, we remember the column they started
   * in so a subsequent Enter returns to it on the next row (standard
   * spreadsheet behavior). Any non-Tab navigation clears this anchor.
   */
  let tabAnchorCol: number | null = null;

  /** Value bar above the grid (built once the grid exists; null when config disables it). */
  let valueBar: CellInputBar | null = null;
  /**
   * Coordinate whose value-bar text is not committed yet. The bar mirrors its text
   * into that cell's in-cell `<input>`, so the existing commit paths (selection
   * change, paste, undo, toolbar, copy) pick a bar edit up without extra wiring.
   */
  let barDirtyCoord: string | null = null;
  let valueBarSyncScheduled = false;

  const persistError = createPersistErrorController({ cells, dataRowCount, dataColumnCount });

  let remotePresenceList: RemoteCollabPeer[] = [];
  const presenceChipEls = new Map<string, HTMLDivElement>();
  /** Cap chips per cell; remaining peers collapse into a "+N" badge. */
  const PRESENCE_CHIP_MAX = 3;
  const PHOSPHOR_NAME_RX = /^[a-z0-9-]+$/;

  function clearRemotePresenceDecor(): void {
    for (const el of cells.values()) {
      el.classList.remove('sheet-cell--remote-presence');
      el.style.removeProperty('--remote-collab-hue');
    }
    for (const chip of presenceChipEls.values()) {
      chip.remove();
    }
    presenceChipEls.clear();
  }

  function renderPresenceChips(peers: readonly RemoteCollabPeer[]): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'sheet-cell-presence-chips';
    wrap.setAttribute('aria-hidden', 'true');
    const shown = peers.slice(0, PRESENCE_CHIP_MAX);
    for (const p of shown) {
      const chip = document.createElement('span');
      chip.className = 'sheet-cell-presence-chip';
      chip.style.setProperty('--peer-hue', String(p.markerHue));
      const label = p.peerLabel?.trim();
      if (label) chip.title = label;
      const iconName = p.peerIcon?.trim().toLowerCase();
      if (iconName && PHOSPHOR_NAME_RX.test(iconName) && iconName.length <= 48) {
        const i = document.createElement('i');
        i.className = `ph ph-${iconName}`;
        i.setAttribute('aria-hidden', 'true');
        chip.appendChild(i);
      } else if (label) {
        chip.textContent = label.charAt(0).toUpperCase();
      }
      wrap.appendChild(chip);
    }
    const overflow = peers.length - shown.length;
    if (overflow > 0) {
      const more = document.createElement('span');
      more.className = 'sheet-cell-presence-chip sheet-cell-presence-chip--more';
      more.textContent = `+${overflow}`;
      wrap.appendChild(more);
    }
    return wrap;
  }

  function paintRemotePresenceOverlays(): void {
    clearRemotePresenceDecor();
    const byKey = new Map<string, RemoteCollabPeer[]>();
    for (const p of remotePresenceList) {
      if (p.row > dataRowCount || p.col > dataColumnCount) continue;
      const span = coveringSpan(p.row, p.col);
      const row = span ? span.row : p.row;
      const col = span ? span.col : p.col;
      const k = cellKey(row, col);
      const arr = byKey.get(k);
      if (arr) arr.push(p);
      else byKey.set(k, [p]);
    }
    for (const [k, plist] of byKey) {
      const el = cells.get(k);
      if (!el) continue;
      el.classList.add('sheet-cell--remote-presence');
      el.style.setProperty('--remote-collab-hue', String(plist[0]!.markerHue));
      const chips = renderPresenceChips(plist);
      el.appendChild(chips);
      presenceChipEls.set(k, chips);
    }
  }

  function getCollabPresencePayload(): CollabPresencePayload | null {
    if (dataRowCount < 1 || dataColumnCount < 1) return null;
    const row = active.row;
    const col = active.col;
    let mode: CollabPresenceMode = 'navigate';
    if (!selectArea.active) {
      const cell = cells.get(cellKey(row, col));
      const inp = cell ? getCellEditor(cell) : null;
      if (inp && document.activeElement === inp) {
        mode = 'edit';
      }
    }
    return { row, col, mode };
  }

  function setRemoteCollabPresence(peers: readonly RemoteCollabPeer[]): void {
    remotePresenceList = peers.map((p) => ({
      ...p,
      markerHue: Number.isFinite(p.markerHue) ? (((p.markerHue % 360) + 360) % 360) : 210,
    }));
    paintRemotePresenceOverlays();
  }

  const selectionChangeListeners = new Set<() => void>();
  const historyChangeListeners = new Set<() => void>();

  function notifySelectionChange(): void {
    for (const fn of selectionChangeListeners) {
      fn();
    }
    syncValueBarSoon();
  }

  /**
   * Coalesced bar refresh. Selection gestures mutate `active` / `selectArea` in
   * several steps, so the bar reads the final state on the next microtask instead
   * of mirroring an intermediate coordinate.
   */
  function syncValueBarSoon(): void {
    if (valueBarSyncScheduled) return;
    valueBarSyncScheduled = true;
    queueMicrotask(() => {
      valueBarSyncScheduled = false;
      syncValueBar();
    });
  }

  function notifyHistoryChange(): void {
    for (const fn of historyChangeListeners) {
      fn();
    }
  }

  let persistActiveInputSkipHistory = false;

  let dragPointerId: number | null = null;
  let dragging = false;
  let dragAnchor = { row: 1, col: 1 };
  let dragMoved = false;
  let dragRaf: number | null = null;
  let dragPendingEnd: { row: number; col: number } | null = null;
  /** Last pointer position observed during an active drag, in client coords;
   *  used by the edge-autoscroll loop to keep growing the selection while the
   *  cursor sits past the viewport. */
  let dragLastPointer: { clientX: number; clientY: number } | null = null;
  let autoScrollRaf: number | null = null;

  function clampRow(row: number): number {
    return Math.max(1, Math.min(dataRowCount, row));
  }

  function clampCol(col: number): number {
    return Math.max(1, Math.min(dataColumnCount, col));
  }

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2;
  let zoomFactor = 1;
  const zoomChangeListeners = new Set<() => void>();

  function notifyZoomChange(): void {
    for (const fn of zoomChangeListeners) fn();
  }

  function getZoom(): number {
    return zoomFactor;
  }

  function setZoom(factor: number): number {
    const clamped = Number.isFinite(factor)
      ? Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, factor))
      : 1;
    const rounded = Math.round(clamped * 100) / 100;
    if (rounded === zoomFactor) return zoomFactor;
    zoomFactor = rounded;
    // Scale the grid content only; popovers are siblings positioned via scaled rects.
    gridInner.style.setProperty('zoom', String(zoomFactor));
    syncGhostColumnsToViewport();
    scheduleSpillRefreshAll();
    notifyZoomChange();
    return zoomFactor;
  }

  const initSel = config.initialSelection;
  if (
    initSel &&
    typeof initSel.row === 'number' &&
    Number.isFinite(initSel.row) &&
    typeof initSel.col === 'number' &&
    Number.isFinite(initSel.col)
  ) {
    active.row = clampRow(Math.trunc(initSel.row));
    active.col = clampCol(Math.trunc(initSel.col));
  }
  selectArea.row = active.row;
  selectArea.col = active.col;
  selectArea.rowEnd = active.row;
  selectArea.colEnd = active.col;
  selectArea.active = false;

  function columnAt(col: number): SpreadsheetColumn | undefined {
    return columns[col - 1];
  }

  function isHiddenCol(col: number): boolean {
    return isHiddenColumn(columns, col);
  }

  function isVisibleCol(col: number): boolean {
    return isVisibleColumn(columns, col);
  }

  function firstVisibleCol(): number {
    return firstVisibleColumnIndex(columns);
  }

  function lastVisibleCol(): number {
    return lastVisibleColumnIndex(columns);
  }

  function clampColVisible(col: number): number {
    return clampToVisibleColumn(columns, clampCol(col));
  }

  function stepVisibleCol(col: number, delta: number): number {
    return stepVisibleColumn(columns, clampCol(col), delta);
  }

  active.col = clampColVisible(active.col);
  selectArea.col = active.col;
  selectArea.colEnd = active.col;
  const initialCovering = coveringSpan(active.row, active.col);
  if (initialCovering) {
    active.row = initialCovering.row;
    active.col = clampColVisible(initialCovering.col);
    selectArea.row = active.row;
    selectArea.col = active.col;
    selectArea.rowEnd = active.row;
    selectArea.colEnd = active.col;
  }

  function isReadOnlyCol(col: number): boolean {
    if (col > dataColumnCount) return true;
    return columnAt(col)?.readOnly === true;
  }

  /** Link columns are action buttons: the grid never edits their URL. */
  function isEditableCol(col: number): boolean {
    if (isReadOnlyCol(col)) return false;
    return !isLinkColumn(columnAt(col));
  }

  function activateLinkCell(row: number, col: number): boolean {
    if (isGhostRow(row) || !isLinkColumn(columnAt(col))) return false;
    const link = cells.get(cellKey(row, col))?.querySelector<HTMLElement>('a.sheet-cell-link');
    if (!link) return false;
    link.click();
    return true;
  }

  function rejectionMessageFor(col: SpreadsheetColumn | undefined): string {
    const vt = columnValueType(col);
    if (vt === 'number') return 'Enter a number';
    if (vt === 'link') return 'Enter a valid link';
    return 'Pick a value from the list';
  }

  /**
   * Commit whatever the editor of (row, col) holds. `editMode` covers in-cell
   * editing; a value-bar edit marks its coordinate dirty and mirrors the text into
   * the same `<input>`, so bar edits commit through here too — including after
   * focus has left the cell (click-away, undo, toolbar, paste).
   */
  function persistCellEdit(row: number, col: number): void {
    if (isGhostRow(row) || isReadOnlyCol(col)) return;
    const key = cellKey(row, col);
    if (!editMode && barDirtyCoord !== key) return;
    const el = cells.get(key);
    if (!el) return;
    const input = getCellEditor(el);
    const content = el.querySelector<HTMLDivElement>('.sheet-cell-content');
    if (!input || !content) return;
    const colDef = columnAt(col);
    const parsed = parseCommittedCellValue(colDef, input.value);
    if (!parsed.ok) {
      const stored = data.get(row, col);
      input.value = stored !== undefined ? String(stored) : '';
      showInputRejectedHint(row, col, rejectionMessageFor(colDef));
      return;
    }
    const recordHistory = history !== null && !persistActiveInputSkipHistory;
    const before = recordHistory ? captureSnapshot([key]) : null;
    data.set(row, col, parsed.value);
    applyCellDisplay(content, colDef, String(parsed.value));
    syncCellChrome(row, col);
    if (barDirtyCoord === key) {
      barDirtyCoord = null;
      valueBar?.setStatus(null);
    }
    if (recordHistory && before) {
      const after = captureSnapshot([key]);
      pushHistoryIfChanged(before, after);
    }
  }

  function persistActiveInput(): void {
    persistCellEdit(active.row, active.col);
  }

  function flushActiveInputSkippingHistory(): void {
    persistActiveInputSkipHistory = true;
    try {
      persistActiveInput();
    } finally {
      persistActiveInputSkipHistory = false;
    }
  }

  let imageLayer: FloatingImageLayer | null = null;

  function commitFloatingImages(before: readonly FloatingImage[], after: readonly FloatingImage[]): void {
    if (!data.setFloatingImages) return;
    if (floatingImagesEqual(before, after)) return;
    data.setFloatingImages(cloneFloatingImages(after));
    if (history) {
      history.pushRecord(new Map(), new Map(), undefined, { before, after });
    }
  }

  /** Undo and redo persist, so a reload matches the screen. */
  function restoreHistoryImages(images: readonly FloatingImage[] | undefined): void {
    if (!images || !data.setFloatingImages) return;
    data.setFloatingImages(cloneFloatingImages(images));
    imageLayer?.sync();
  }

  function doUndo(): boolean {
    if (!history || !data.replaceCell) return false;
    flushActiveInputSkippingHistory();
    const rec = history.undo();
    if (!rec) return false;
    applySnapshot(rec.before);
    restoreHistorySpans(rec.spansBefore);
    restoreHistoryImages(rec.imagesBefore);
    hydrateFromHistoryRecord(rec.before, rec.after);
    layoutCellSpans();
    snapActiveToSpanAnchor();
    notifySelectionChange();
    return true;
  }

  function doRedo(): boolean {
    if (!history || !data.replaceCell) return false;
    flushActiveInputSkippingHistory();
    const rec = history.redo();
    if (!rec) return false;
    applySnapshot(rec.after);
    restoreHistorySpans(rec.spansAfter);
    restoreHistoryImages(rec.imagesAfter);
    hydrateFromHistoryRecord(rec.before, rec.after);
    layoutCellSpans();
    snapActiveToSpanAnchor();
    notifySelectionChange();
    return true;
  }

  function restoreHistorySpans(spans: readonly CellSpan[] | undefined): void {
    if (!spans || !data.setCellSpans) return;
    const next = cloneCellSpans(spans);
    const run = () => data.setCellSpans!(next);
    if (config.suppressOutboundSyncDuring) config.suppressOutboundSyncDuring(run);
    else run();
  }

  /** Move the active cell onto the anchor when it sits on a covered coordinate. */
  function snapActiveToSpanAnchor(): void {
    const span = coveringSpan(active.row, active.col);
    if (!span || (span.row === active.row && span.col === active.col)) return;
    setActive(span.row, span.col);
  }

  function updateCommentIndicator(row: number, col: number): void {
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    const k = cellKey(row, col);
    const note = data.getStoredCell?.(row, col)?.comment?.trim();
    el.classList.toggle('sheet-cell--has-comment', Boolean(note));
    if (!note) {
      if (commentPreviewKey === k) hideCommentPreview();
      el.querySelector('.sheet-cell-comment-hit')?.remove();
      return;
    }
    let hit = el.querySelector<HTMLButtonElement>('.sheet-cell-comment-hit');
    if (!hit) {
      hit = document.createElement('button');
      hit.type = 'button';
      hit.className = 'sheet-cell-comment-hit';
      hit.setAttribute('aria-label', 'Show comment');
      hit.setAttribute('aria-expanded', 'false');
      hit.setAttribute('aria-controls', 'sheet-comment-preview');
      el.appendChild(hit);
    }
    hit.dataset.row = String(row);
    hit.dataset.col = String(col);
  }

  function syncCellChrome(row: number, col: number): void {
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    applyCellInlineStyleRecord(el, data.getCellStyle?.(row, col));
    updateCommentIndicator(row, col);
    // Style/value changes move both the wrap flag and what the row can spill into.
    applyWrapClass(row, col);
    refreshSpillAround(row, col);
    scheduleTextFitPass();
  }

  let commentEditorAnchor: { row: number; col: number } | null = null;

  function cellInitIsVacuous(cell: { value: string | number; style?: object; comment?: string }): boolean {
    const v = cell.value;
    const hasValue =
      typeof v === 'number'
        ? Number.isFinite(v)
        : String(v).trim().length > 0;
    const hasStyle = Boolean(cell.style && Object.keys(cell.style).length > 0);
    const hasComment = Boolean(cell.comment && cell.comment.trim());
    return !hasValue && !hasStyle && !hasComment;
  }

  function commitCommentAt(row: number, col: number, text: string | undefined): void {
    if (!data.replaceCell || !data.getStoredCell) return;
    const key = cellKey(row, col);
    const before = history ? captureSnapshot([key]) : null;
    const prev = data.getStoredCell(row, col);
    const raw = data.get(row, col);
    const value = prev !== undefined ? prev.value : (raw !== undefined ? raw : '');
    const style = prev?.style;
    const trimmed = (text ?? '').trim();
    const comment = trimmed === '' ? undefined : trimmed;
    const next: SpreadsheetCellInit = { value };
    if (style !== undefined && Object.keys(style).length > 0) next.style = { ...style };
    if (comment !== undefined) next.comment = comment;
    data.replaceCell(row, col, cellInitIsVacuous(next) ? null : next);
    hydrateCellFromStore(row, col);
    if (history && before) {
      const after = captureSnapshot([key]);
      pushHistoryIfChanged(before, after);
    }
  }

  let commentPopover: HTMLDivElement | null = null;
  let commentTextarea: HTMLTextAreaElement | null = null;

  function positionCommentPopover(row: number, col: number): void {
    if (!commentPopover) return;
    const cell = cells.get(cellKey(row, col));
    if (!cell) return;
    const cr = cell.getBoundingClientRect();
    const margin = 8;
    const w = Math.min(320, window.innerWidth - margin * 2);
    let left = cr.left;
    if (left + w > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - w);
    }
    commentPopover.style.width = `${w}px`;
    commentPopover.style.left = `${left}px`;
    commentPopover.style.top = `${Math.min(cr.bottom + 4, window.innerHeight - margin - 160)}px`;
  }

  function closeCommentEditor(apply: boolean): void {
    hideCommentPreview();
    if (!commentPopover || !commentTextarea || !commentEditorAnchor) {
      commentEditorAnchor = null;
      if (commentPopover) commentPopover.hidden = true;
      return;
    }
    const { row, col } = commentEditorAnchor;
    if (apply) {
      commitCommentAt(row, col, commentTextarea.value);
    }
    commentTextarea.value = '';
    commentPopover.hidden = true;
    commentEditorAnchor = null;
    viewport.focus();
  }

  function openCommentEditor(): void {
    if (!commentsEnabled || !commentPopover || !commentTextarea) return;
    hideCommentPreview();
    persistActiveInput();
    hideSuggestions();
    const anchor = getPasteAnchor();
    commentEditorAnchor = { row: anchor.row, col: anchor.col };
    const existing = data.getStoredCell?.(anchor.row, anchor.col)?.comment ?? '';
    commentTextarea.value = existing;
    commentPopover.hidden = false;
    positionCommentPopover(anchor.row, anchor.col);
    commentTextarea.focus();
  }

  function anchorOf(row: number, col: number): { row: number; col: number } {
    const span = coveringSpan(row, col);
    if (!span) return { row, col };
    return { row: span.row, col: span.col };
  }

  function getFormattingTargets(): { row: number; col: number }[] {
    const out: { row: number; col: number }[] = [];
    const seen = new Set<string>();
    const push = (row: number, col: number) => {
      const anchor = anchorOf(row, col);
      if (isGhostRow(anchor.row) || isReadOnlyCol(anchor.col)) return;
      const key = cellKey(anchor.row, anchor.col);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(anchor);
    };
    if (selectArea.active) {
      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      for (let row = minRow; row <= maxRow; row++) {
        if (isGhostRow(row)) continue;
        for (let col = minCol; col <= maxCol; col++) {
          if (!isHiddenCol(col)) push(row, col);
        }
      }
      return out;
    }
    if (!isGhostRow(active.row) && !isReadOnlyCol(active.col)) push(active.row, active.col);
    return out;
  }

  function currentSelectionRect(): CellSpan {
    if (selectArea.active) {
      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      return {
        row: minRow,
        col: minCol,
        rowSpan: maxRow - minRow + 1,
        colSpan: maxCol - minCol + 1,
      };
    }
    return { row: active.row, col: active.col, rowSpan: 1, colSpan: 1 };
  }

  function rectCanMerge(rect: CellSpan): boolean {
    if (rect.rowSpan === 1 && rect.colSpan === 1) return false;
    let count = 0;
    for (let row = rect.row; row < rect.row + rect.rowSpan; row++) {
      if (row < 1 || row > dataRowCount || isGhostRow(row)) return false;
      for (let col = rect.col; col < rect.col + rect.colSpan; col++) {
        if (col < 1 || col > dataColumnCount || isHiddenCol(col)) return false;
        if (isReadOnlyCol(col) || isLinkColumn(columnAt(col))) return false;
        count++;
      }
    }
    return count >= 2;
  }

  function canMergeCells(): boolean {
    if (!data.setCellSpans) return false;
    const rect = currentSelectionRect();
    if (!rectCanMerge(rect)) return false;
    const already = currentSpans().some(
      (span) =>
        span.row === rect.row &&
        span.col === rect.col &&
        span.rowSpan === rect.rowSpan &&
        span.colSpan === rect.colSpan,
    );
    return !already;
  }

  function canUnmergeCells(): boolean {
    if (!data.setCellSpans) return false;
    const rect = currentSelectionRect();
    return currentSpans().some((span) => spansOverlap(span, rect));
  }

  function writeCellSpans(next: readonly CellSpan[], clearKeys: readonly string[]): void {
    if (!data.setCellSpans) return;
    const spansBefore = cloneCellSpans(currentSpans());
    const spansAfter = cloneCellSpans(next);
    const before = history ? captureSnapshot(clearKeys) : null;
    for (const key of clearKeys) {
      const { row, col } = keyToRowCol(key);
      if (data.replaceCell) data.replaceCell(row, col, null);
      else data.set(row, col, '');
      hydrateCellFromStore(row, col);
    }
    data.setCellSpans(spansAfter);
    layoutCellSpans();
    if (history && before) {
      history.pushRecord(before, captureSnapshot(clearKeys), {
        before: spansBefore,
        after: spansAfter,
      });
    }
    notifySelectionChange();
  }

  function mergeCells(): boolean {
    if (!canMergeCells()) return false;
    persistActiveInput();
    const rect = currentSelectionRect();
    writeCellSpans(mergeSpanList(currentSpans(), rect), coveredCellKeys(rect));
    setActive(rect.row, rect.col);
    return true;
  }

  function unmergeCells(): boolean {
    if (!canUnmergeCells()) return false;
    persistActiveInput();
    const rect = currentSelectionRect();
    writeCellSpans(unmergeSpanList(currentSpans(), rect), []);
    return true;
  }

  function applyExternalSpans(spans: readonly CellSpan[]): boolean {
    if (!data.setCellSpans) return false;
    data.setCellSpans(cloneCellSpans(spans));
    layoutCellSpans();
    snapActiveToSpanAnchor();
    notifySelectionChange();
    return true;
  }

  function mergeCellStyleOnSelection(patch: Record<string, string | undefined>): void {
    if (!data.mergeCellStyle) return;
    persistActiveInput();
    const targets = getFormattingTargets();
    if (targets.length === 0) return;
    const keys = targets.map(({ row, col }) => cellKey(row, col));
    const before = history ? captureSnapshot(keys) : null;
    for (const { row, col } of targets) {
      data.mergeCellStyle(row, col, patch);
      syncCellChrome(row, col);
    }
    if (history && before) {
      const after = captureSnapshot(keys);
      pushHistoryIfChanged(before, after);
    }
    notifySelectionChange();
  }

  function mergeCellStyleOnEachTarget(
    patchForCell: (row: number, col: number) => Record<string, string | undefined>,
  ): void {
    if (!data.mergeCellStyle) return;
    persistActiveInput();
    const targets = getFormattingTargets();
    if (targets.length === 0) return;
    const keys = targets.map(({ row, col }) => cellKey(row, col));
    const before = history ? captureSnapshot(keys) : null;
    for (const { row, col } of targets) {
      data.mergeCellStyle(row, col, patchForCell(row, col));
      syncCellChrome(row, col);
    }
    if (history && before) {
      const after = captureSnapshot(keys);
      pushHistoryIfChanged(before, after);
    }
    notifySelectionChange();
  }

  function everyTargetCellStyle(
    cssProperty: string,
    predicate: (value: string | undefined) => boolean,
  ): boolean {
    const targets = getFormattingTargets();
    if (targets.length === 0) return false;
    return targets.every(({ row, col }) => {
      const v = data.getCellStyle?.(row, col)?.[cssProperty];
      return predicate(v);
    });
  }

  function uniformTargetCellStyle(cssProperty: string): string | null {
    const targets = getFormattingTargets();
    if (targets.length === 0) return null;
    let current: string | null | undefined;
    for (const { row, col } of targets) {
      const raw = data.getCellStyle?.(row, col)?.[cssProperty];
      const next = raw ? (canonicalCssColor(raw) ?? raw) : null;
      if (current === undefined) current = next;
      else if (current !== next) return null;
    }
    return current ?? null;
  }

  function colorsUsedInSheet(cssProperty: 'color' | 'background-color'): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    const add = (raw: string | undefined) => {
      if (!raw) return;
      const color = canonicalCssColor(raw);
      if (!color || seen.has(color)) return;
      seen.add(color);
      out.push(color);
    };
    const found: string[] = [];
    const take = (style: SpreadsheetCellStyleDeclarations | undefined) => {
      const raw = style?.[cssProperty];
      if (raw) found.push(raw);
    };
    if (typeof data.forEachStoredCell === 'function') {
      data.forEachStoredCell((cell) => take(cell.style));
    } else if (data.getCellStyle) {
      for (let row = 1; row <= dataRowCount; row++) {
        for (let col = 1; col <= dataColumnCount; col++) {
          take(data.getCellStyle(row, col));
        }
      }
    }
    for (let i = found.length - 1; i >= 0; i--) add(found[i]);
    const optionKey = cssProperty === 'color' ? 'color' : 'backgroundColor';
    for (const column of columns) {
      for (const opt of column.selectOptions ?? []) {
        add(opt[optionKey]);
      }
    }
    return out;
  }

  function doOnEverySelectedCell(callback: (cell: HTMLDivElement) => void): void {
    const minRow = Math.min(selectArea.row, selectArea.rowEnd);
    const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
    const minCol = Math.min(selectArea.col, selectArea.colEnd);
    const maxCol = Math.max(selectArea.col, selectArea.colEnd);
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const cell = cells.get(cellKey(row, col));
        if (!cell) continue;
        callback(cell);
      }
    }
  }

  function getPasteAnchor(): { row: number; col: number } {
    if (selectArea.active) {
      return {
        row: Math.min(selectArea.row, selectArea.rowEnd),
        col: Math.min(selectArea.col, selectArea.colEnd),
      };
    }
    return { row: active.row, col: active.col };
  }

  function formatCellValueForClipboard(row: number, col: number): string {
    const v = data.get(row, col);
    if (v === undefined) return '';
    const colDef = columnAt(col);
    if (isSelectColumn(colDef)) {
      return resolveSelectLabel(colDef, String(v));
    }
    return String(v);
  }

  function buildCopyPlainText(): string {
    persistActiveInput();
    if (selectArea.active) {
      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      const lines: string[] = [];
      for (let r = minRow; r <= maxRow; r++) {
        const parts: string[] = [];
        for (let c = minCol; c <= maxCol; c++) {
          if (isHiddenCol(c)) continue;
          parts.push(escapeTsvField(formatCellValueForClipboard(r, c)));
        }
        lines.push(parts.join('\t'));
      }
      return lines.join('\n');
    }
    return formatCellValueForClipboard(active.row, active.col);
  }

  function buildRowPlainText(targetRow: number): string {
    const r = clampRow(targetRow);
    const parts: string[] = [];
    for (let c = 1; c <= dataColumnCount; c++) {
      if (isHiddenCol(c)) continue;
      parts.push(escapeTsvField(formatCellValueForClipboard(r, c)));
    }
    return parts.join('\t');
  }

  function mutateClearWritableCellsInRow(targetRow: number): void {
    if (!data.replaceCell) return;
    const row = clampRow(targetRow);
    const keys: string[] = [];
    for (let col = 1; col <= dataColumnCount; col++) {
      if (!isReadOnlyCol(col)) keys.push(cellKey(row, col));
    }
    if (keys.length === 0) return;
    const spansBefore = cloneCellSpans(currentSpans());
    const spansAfter = spansAfterClearingRow(spansBefore, row);
    const spanChange = !cellSpansEqual(spansBefore, spansAfter);
    const before = history ? captureSnapshot(keys) : null;
    for (const k of keys) {
      const { row: rr, col } = keyToRowCol(k);
      data.replaceCell(rr, col, null);
      hydrateCellFromStore(rr, col);
    }
    if (spanChange && data.setCellSpans) {
      data.setCellSpans(spansAfter);
      layoutCellSpans();
    }
    if (history && before) {
      const after = captureSnapshot(keys);
      if (spanChange) {
        history.pushRecord(before, after, { before: spansBefore, after: spansAfter });
      } else {
        pushHistoryIfChanged(before, after);
      }
    }
  }

  function clearSheetRow(targetRow: number): void {
    if (!data.replaceCell) return;
    const tr = Math.trunc(targetRow);
    if (!Number.isFinite(tr) || tr < 1 || tr > dataRowCount) return;
    const row = tr;
    persistActiveInput();
    let anyWritable = false;
    for (let col = 1; col <= dataColumnCount; col++) {
      if (!isReadOnlyCol(col)) {
        anyWritable = true;
        break;
      }
    }
    if (!anyWritable) return;
    const runMutate = () => mutateClearWritableCellsInRow(row);
    if (suppressOutboundSyncDuring) {
      suppressOutboundSyncDuring(runMutate);
    } else {
      runMutate();
    }
    onRowDeleted?.(row);
    notifySelectionChange();
  }

  function applyRemoteRowClear(targetRow: number): void {
    if (!data.replaceCell) return;
    const r = Math.trunc(targetRow);
    if (!Number.isFinite(r) || r < 1 || r > dataRowCount) return;
    persistActiveInput();
    mutateClearWritableCellsInRow(r);
    notifySelectionChange();
  }

  function shouldLetInputHandleCopy(evTarget: EventTarget | null): boolean {
    if (!(evTarget instanceof HTMLInputElement)) return false;
    if (!evTarget.classList.contains('sheet-cell-input')) return false;
    if (selectArea.active) return false;
    const a = evTarget.selectionStart ?? 0;
    const b = evTarget.selectionEnd ?? 0;
    return a !== b;
  }

  function shouldLetInputHandlePaste(evTarget: EventTarget | null, plain: string): boolean {
    if (!(evTarget instanceof HTMLInputElement)) return false;
    if (!evTarget.classList.contains('sheet-cell-input')) return false;
    if (selectArea.active) return false;
    const gridLike = plain.includes('\t') || plain.includes('\n');
    return !gridLike;
  }

  function collectPasteTargetKeys(
    anchorRow: number,
    anchorCol: number,
    grid: string[][],
  ): string[] {
    const keys: string[] = [];
    for (let dr = 0; dr < grid.length; dr++) {
      const row = grid[dr]!;
      for (let dc = 0; dc < row.length; dc++) {
        const r = anchorRow + dr;
        const c = anchorCol + dc;
        if (r < 1 || r > dataRowCount || c < 1 || c > dataColumnCount) continue;
        if (!isEditableCol(c) || isCoveredCell(r, c)) continue;
        keys.push(cellKey(r, c));
      }
    }
    return keys;
  }

  function applyPastedGrid(plain: string): void {
    const grid = parseClipboardRows(plain);
    if (grid.length === 0) return;

    clearCopyMarquee();
    hideSuggestions();
    persistActiveInput();

    const anchor = getPasteAnchor();
    const maxR = anchor.row + grid.length - 1;
    if (maxR > dataRowCount && growRowCountForPaste) {
      growRowCountForPaste({ minRowCount: maxR, plain });
      return;
    }

    const keys = collectPasteTargetKeys(anchor.row, anchor.col, grid);
    const before = history && keys.length > 0 ? captureSnapshot(keys) : null;

    for (let dr = 0; dr < grid.length; dr++) {
      const row = grid[dr]!;
      for (let dc = 0; dc < row.length; dc++) {
        const r = anchor.row + dr;
        const c = anchor.col + dc;
        if (r < 1 || r > dataRowCount || c < 1 || c > dataColumnCount) continue;
        if (!isEditableCol(c) || isCoveredCell(r, c)) continue;
        const raw = row[dc] ?? '';
        const colDef = columnAt(c);
        const parsed = parseCommittedCellValue(colDef, raw);
        if (!parsed.ok) continue;
        data.set(r, c, parsed.value);
        hydrateCellFromStore(r, c);
      }
    }

    if (history && before) {
      const after = captureSnapshot(keys);
      pushHistoryIfChanged(before, after);
    }
    notifySelectionChange();
  }

  const root = document.createElement('div');
  root.className = 'sheet-root';
  root.id = 'sheet-container';

  const viewport = document.createElement('div');
  viewport.className = 'sheet-viewport';
  viewport.tabIndex = 0;
  viewport.setAttribute('role', 'grid');
  viewport.setAttribute('aria-label', 'Spreadsheet');
  viewport.setAttribute('aria-rowcount', String(dataRowCount + 1));
  viewport.setAttribute('aria-colcount', String(visibleColumnCount(columns)));

  srAnnounceEl = document.createElement('div');
  srAnnounceEl.className = 'sheet-sr-live';
  srAnnounceEl.setAttribute('aria-live', 'polite');
  srAnnounceEl.setAttribute('aria-atomic', 'true');

  const inputHintEl = document.createElement('div');
  inputHintEl.className = 'sheet-input-hint';
  inputHintEl.setAttribute('role', 'status');
  inputHintEl.hidden = true;
  let inputHintTimer: ReturnType<typeof setTimeout> | null = null;

  function hideInputHint(): void {
    if (inputHintTimer !== null) {
      clearTimeout(inputHintTimer);
      inputHintTimer = null;
    }
    inputHintEl.hidden = true;
    inputHintEl.classList.remove('sheet-input-hint--visible');
  }

  function showInputRejectedHint(row: number, col: number, message: string): void {
    const cell = cells.get(cellKey(row, col));
    if (!cell) return;
    if (inputHintTimer !== null) clearTimeout(inputHintTimer);
    inputHintEl.textContent = message;
    inputHintEl.hidden = false;
    inputHintEl.classList.add('sheet-input-hint--visible');
    const r = cell.getBoundingClientRect();
    const margin = 8;
    inputHintEl.style.left = '0px';
    inputHintEl.style.top = '0px';
    const hw = inputHintEl.offsetWidth;
    let left = r.left;
    if (left + hw > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - hw);
    }
    inputHintEl.style.left = `${Math.round(left)}px`;
    inputHintEl.style.top = `${Math.round(r.bottom + 4)}px`;
    inputHintTimer = window.setTimeout(hideInputHint, 2200);
  }

  function showFloatingHint(message: string): void {
    if (inputHintTimer !== null) clearTimeout(inputHintTimer);
    inputHintEl.textContent = message;
    inputHintEl.hidden = false;
    inputHintEl.classList.add('sheet-input-hint--visible');
    const r = viewport.getBoundingClientRect();
    inputHintEl.style.left = `${Math.round(r.left + 12)}px`;
    inputHintEl.style.top = `${Math.round(r.top + 12)}px`;
    inputHintTimer = window.setTimeout(hideInputHint, 2200);
  }

  const commentPreviewPop = document.createElement('div');
  commentPreviewPop.className = 'sheet-comment-preview';
  commentPreviewPop.id = 'sheet-comment-preview';
  commentPreviewPop.hidden = true;
  commentPreviewPop.setAttribute('role', 'tooltip');

  let commentPreviewHideTimer: ReturnType<typeof setTimeout> | null = null;
  let commentPreviewKey: string | null = null;
  let commentPreviewSourceHit: HTMLButtonElement | null = null;

  function hideCommentPreview(): void {
    if (commentPreviewHideTimer !== null) {
      clearTimeout(commentPreviewHideTimer);
      commentPreviewHideTimer = null;
    }
    commentPreviewPop.hidden = true;
    commentPreviewKey = null;
    commentPreviewPop.textContent = '';
    if (commentPreviewSourceHit) {
      commentPreviewSourceHit.setAttribute('aria-expanded', 'false');
      commentPreviewSourceHit = null;
    }
  }

  function positionCommentPreview(anchor: HTMLElement): void {
    const ar = anchor.getBoundingClientRect();
    const margin = 8;
    const maxW = Math.min(360, window.innerWidth - margin * 2);
    const maxH = Math.min(280, window.innerHeight - margin * 2);
    commentPreviewPop.style.maxWidth = `${maxW}px`;
    commentPreviewPop.style.maxHeight = `${maxH}px`;
    commentPreviewPop.style.left = '0px';
    commentPreviewPop.style.top = '0px';
    void commentPreviewPop.offsetWidth;
    const ph = commentPreviewPop.getBoundingClientRect().height;
    const pw = Math.min(maxW, commentPreviewPop.getBoundingClientRect().width);
    let top = ar.bottom + 4;
    let left = ar.left;
    if (top + ph > window.innerHeight - margin) {
      top = Math.max(margin, ar.top - ph - 4);
    }
    if (left + pw > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - pw);
    }
    commentPreviewPop.style.left = `${left}px`;
    commentPreviewPop.style.top = `${top}px`;
  }

  function scheduleHideCommentPreview(): void {
    if (commentPreviewHideTimer !== null) clearTimeout(commentPreviewHideTimer);
    commentPreviewHideTimer = setTimeout(() => {
      commentPreviewHideTimer = null;
      hideCommentPreview();
    }, 200);
  }

  function showCommentPreviewFromHit(hit: HTMLButtonElement): void {
    if (commentPopover && !commentPopover.hidden) return;
    const row = clampRow(Number(hit.dataset.row));
    const col = clampCol(Number(hit.dataset.col));
    const note = data.getStoredCell?.(row, col)?.comment?.trim();
    if (!note) {
      hideCommentPreview();
      return;
    }
    if (commentPreviewHideTimer !== null) {
      clearTimeout(commentPreviewHideTimer);
      commentPreviewHideTimer = null;
    }
    commentPreviewPop.textContent = note;
    commentPreviewPop.hidden = false;
    commentPreviewKey = cellKey(row, col);
    if (commentPreviewSourceHit && commentPreviewSourceHit !== hit) {
      commentPreviewSourceHit.setAttribute('aria-expanded', 'false');
    }
    commentPreviewSourceHit = hit;
    hit.setAttribute('aria-expanded', 'true');
    positionCommentPreview(hit);
    requestAnimationFrame(() => positionCommentPreview(hit));
  }

  function onCopyCapture(e: ClipboardEvent): void {
    if (!viewport.contains(e.target as Node)) return;
    if (shouldLetInputHandleCopy(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    const text = buildCopyPlainText();
    e.clipboardData?.setData('text/plain', text);
    setInternalClipboardPlain(text);
    setCopyMarqueeFromSelection();
  }

  function onPasteCapture(e: ClipboardEvent): void {
    if (!viewport.contains(e.target as Node)) return;
    const plain = e.clipboardData?.getData('text/plain') ?? '';
    if (shouldLetInputHandlePaste(e.target, plain)) return;
    let payload = plain;
    if (payload === '') {
      const html = e.clipboardData?.getData('text/html') ?? '';
      const fromHtml = parseHtmlClipboardTable(html);
      if (fromHtml) payload = fromHtml;
    }
    if (payload === '') payload = getInternalClipboardPlain();
    if (payload === '') return;
    e.preventDefault();
    e.stopPropagation();
    applyPastedGrid(payload);
  }

  function clearWritableCellsInActiveRange(): void {
    const keys: string[] = [];
    if (selectArea.active) {
      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      for (let r = minRow; r <= maxRow; r++) {
        if (r < 1 || r > dataRowCount) continue;
        for (let c = minCol; c <= maxCol; c++) {
          if (c < 1 || c > dataColumnCount) continue;
          if (!isEditableCol(c)) continue;
          keys.push(cellKey(r, c));
        }
      }
    } else if (isEditableCol(active.col) && !isGhostRow(active.row)) {
      keys.push(cellKey(active.row, active.col));
    }
    if (keys.length === 0) return;
    const before = history ? captureSnapshot(keys) : null;
    for (const k of keys) {
      const { row: r, col: c } = keyToRowCol(k);
      data.set(r, c, '');
      const cellEl = cells.get(k);
      if (cellEl) {
        const contentEl = cellEl.querySelector<HTMLDivElement>('.sheet-cell-content');
        if (contentEl) applyCellDisplay(contentEl, columnAt(c), '');
        const inp = getCellEditor(cellEl);
        if (inp) inp.value = '';
        syncCellChrome(r, c);
      }
    }
    if (history && before) {
      const after = captureSnapshot(keys);
      pushHistoryIfChanged(before, after);
    }
  }

  function onCutCapture(e: ClipboardEvent): void {
    if (!viewport.contains(e.target as Node)) return;
    if (shouldLetInputHandleCopy(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    const text = buildCopyPlainText();
    e.clipboardData?.setData('text/plain', text);
    setInternalClipboardPlain(text);
    setCopyMarqueeFromSelection();
    clearWritableCellsInActiveRange();
    syncSelectionHighlight();
  }

  function writeClipboardText(text: string): void {
    // Always keep an in-app buffer so copy/paste still works when the system
    // clipboard is blocked (permission denied, iframe policy, etc.).
    setInternalClipboardPlain(text);
    // Prefer a synchronous execCommand copy while we still have the user gesture.
    // Chrome often exposes clipboard.writeText but rejects it (cross-origin iframes
    // without allow="clipboard-write", Permissions-Policy, etc.); Firefox is more
    // permissive. Falling back after a rejected Promise is too late — the gesture
    // is already gone — so try the sync path first.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'true');
    ta.setAttribute('aria-hidden', 'true');
    ta.dataset.arkClipboard = '1';
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    document.body.appendChild(ta);
    ta.focus({ preventScroll: true });
    ta.select();
    ta.setSelectionRange(0, text.length);
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      /* unsupported */
    }
    ta.remove();
    if (previouslyFocused && previouslyFocused.isConnected) {
      try {
        previouslyFocused.focus({ preventScroll: true });
      } catch {
        previouslyFocused.focus();
      }
    }
    if (copied) return;
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).catch(() => {});
    }
  }

  async function readClipboardPayloadForPaste(): Promise<string> {
    const clip = navigator.clipboard;
    if (clip?.read) {
      try {
        const items = await clip.read();
        for (const item of items) {
          if (item.types.includes('text/plain')) {
            const plain = await (await item.getType('text/plain')).text();
            if (plain) return plain;
          }
        }
        for (const item of items) {
          if (item.types.includes('text/html')) {
            const html = await (await item.getType('text/html')).text();
            const fromHtml = parseHtmlClipboardTable(html);
            if (fromHtml) return fromHtml;
          }
        }
      } catch {
        /* fall through to readText */
      }
    }
    if (clip?.readText) {
      try {
        const plain = await clip.readText();
        if (plain) return plain;
      } catch {
        /* unsupported or denied */
      }
    }
    return getInternalClipboardPlain();
  }

  const gridInner = document.createElement('div');
  gridInner.className = 'sheet-grid-inner';
  gridInner.style.width = `${rowHeaderWidthPx + mountedSheetCellsWidth}px`;

  const headerRow = document.createElement('div');
  headerRow.className = 'sheet-header-row';
  headerRow.style.width = '100%';
  headerRow.setAttribute('role', 'row');
  headerRow.setAttribute('aria-rowindex', '1');

  const headerCorner = document.createElement('div');
  headerCorner.className = 'sheet-header-corner';
  headerCorner.style.width = `${rowHeaderWidthPx}px`;
  headerCorner.setAttribute('aria-hidden', 'true');
  headerRow.appendChild(headerCorner);

  const dataColumnHeaderEls: (HTMLDivElement | undefined)[] = [];
  const columnResizeEnabled = columnWidthPersistenceKey !== undefined;
  for (let c = 0; c < columns.length; c++) {
    if (columns[c]!.hidden) {
      dataColumnHeaderEls[c] = undefined;
      continue;
    }
    const h = document.createElement('div');
    h.className = 'sheet-column-header';
    if (columns[c]!.readOnly) h.classList.add('sheet-column-header--readonly');
    h.style.width = `${columnWidths[c]}px`;
    h.dataset.col = String(c + 1);
    h.setAttribute('role', 'columnheader');
    h.setAttribute('aria-colindex', String(c + 1));

    const headerLabel = document.createElement('span');
    headerLabel.className = 'sheet-column-header-label';
    headerLabel.textContent = columns[c]!.header;
    h.appendChild(headerLabel);

    if (columnResizeEnabled) {
      const grip = document.createElement('div');
      grip.className = 'sheet-column-resize-handle';
      grip.dataset.col = String(c + 1);
      grip.setAttribute('aria-hidden', 'true');
      h.appendChild(grip);
    }

    headerRow.appendChild(h);
    dataColumnHeaderEls[c] = h;
  }

  const gridBody = document.createElement('div');
  gridBody.className = 'sheet-grid-body';
  gridBody.style.height = `${sheetTotalHeight}px`;

  const rowCellsEls: HTMLDivElement[] = [];
  const rowEls: HTMLDivElement[] = [];

  for (let row = 1; row <= mountedRowCount; row++) {
    const rowEl = document.createElement('div');
    rowEl.className = 'sheet-row';
    rowEl.style.height = `${rowHeights[row - 1]}px`;
    rowEl.style.top = `${cumulativeRowHeights[row - 1]}px`;
    if (!isGhostRow(row)) {
      rowEl.setAttribute('role', 'row');
      rowEl.setAttribute('aria-rowindex', String(row + 1));
    }

    const rowGutter = document.createElement('div');
    rowGutter.className = 'sheet-row-gutter';
    rowGutter.style.flex = `0 0 ${rowHeaderWidthPx}px`;
    rowGutter.style.width = `${rowHeaderWidthPx}px`;
    if (isGhostRow(row)) {
      rowGutter.classList.add('sheet-row-gutter--ghost');
      rowGutter.textContent = '';
    } else {
      rowGutter.textContent = String(row);
    }
    rowGutter.dataset.row = String(row);
    rowGutter.setAttribute('aria-hidden', 'true');

    const rowCells = document.createElement('div');
    rowCells.className = 'sheet-row-cells';
    rowCells.style.width = `${mountedSheetCellsWidth}px`;

    const ghost = isGhostRow(row);

    for (let col = 1; col <= dataColumnCount; col++) {
      if (isHiddenCol(col)) continue;
      const cell = document.createElement('div');
      cell.className = 'sheet-cell';
      cell.dataset.row = String(row);
      cell.dataset.col = String(col);
      cell.style.width = `${columnWidths[col - 1]}px`;
      cell.style.left = `${cumulativeColumnWidths[col - 1]}px`;
      if (!ghost) {
        cell.setAttribute('role', 'gridcell');
        cell.setAttribute('aria-rowindex', String(row + 1));
        cell.setAttribute('aria-colindex', String(col + 1));
      }

      const key = cellKey(row, col);
      const raw = ghost ? undefined : data.get(row, col);
      const display = raw !== undefined ? String(raw) : '';

      const content = document.createElement('div');
      content.className = 'sheet-cell-content';
      applyCellDisplay(content, columnAt(col), display);

      const input = document.createElement('input');
      input.className = 'sheet-cell-input';
      input.type = 'text';
      input.autocomplete = 'off';
      const colDef = columnAt(col);
      if (columnValueType(colDef) === 'number') {
        input.inputMode = 'decimal';
        cell.classList.add('sheet-cell--number');
      }
      if (isSelectColumn(colDef)) {
        input.dataset.sheetValueType = 'select';
        cell.classList.add('sheet-cell--select');
      }
      if (isLinkColumn(colDef)) {
        cell.classList.add('sheet-cell--link');
        if (!linkButtonLabel(colDef)) cell.classList.add('sheet-cell--link-icon');
      }
      input.value = display;
      if (ghost || colDef?.readOnly) {
        cell.classList.add('sheet-cell-readonly');
        if (ghost) cell.classList.add('sheet-cell--ghost');
        input.readOnly = true;
        input.tabIndex = -1;
        input.setAttribute('aria-readonly', 'true');
      }

      // Dropdown affordance for editable select cells (shown on hover / active).
      if (!ghost && !colDef?.readOnly && isSelectColumn(colDef)) {
        const caret = document.createElement('span');
        caret.className = 'sheet-cell-select-caret';
        caret.setAttribute('aria-hidden', 'true');
        const ci = document.createElement('i');
        ci.className = 'ph ph-caret-down';
        caret.appendChild(ci);
        cell.appendChild(caret);
      }

      applyCellInlineStyleRecord(cell, ghost ? undefined : data.getCellStyle?.(row, col));

      if (!ghost && row === active.row && col === active.col) {
        cell.classList.add('sheet-cell-active');
        cell.setAttribute('aria-selected', 'true');
      }

      cell.append(content, input);
      cells.set(key, cell);
      if (!ghost) updateCommentIndicator(row, col);
      rowCells.appendChild(cell);
    }
    rowCellsEls.push(rowCells);
    rowEls.push(rowEl);
    rowEl.append(rowGutter, rowCells);
    gridBody.appendChild(rowEl);
  }

  copyMarqueeEl = document.createElement('div');
  copyMarqueeEl.className = 'sheet-copy-marquee';
  copyMarqueeEl.hidden = true;
  copyMarqueeEl.setAttribute('aria-hidden', 'true');
  gridBody.appendChild(copyMarqueeEl);

  selectionOutlineEl = document.createElement('div');
  selectionOutlineEl.className = 'sheet-selection-outline';
  selectionOutlineEl.hidden = true;
  selectionOutlineEl.setAttribute('aria-hidden', 'true');
  gridBody.appendChild(selectionOutlineEl);

  imageLayer = mountFloatingImageLayer({
    gridBody,
    viewport,
    rowHeaderWidthPx,
    getZoom: () => zoomFactor,
    getImages: () => data.getFloatingImages?.() ?? [],
    onCommit: commitFloatingImages,
    showHint: showFloatingHint,
    focusSheet: () => viewport.focus(),
  });

  const ghostColumnHeaderEls: HTMLDivElement[] = [];

  function createGhostColumnHeader(col: number): HTMLDivElement {
    const h = document.createElement('div');
    h.className = 'sheet-column-header sheet-column-header--ghost';
    h.style.width = `${widthOfMountedCol1Based(col)}px`;
    h.setAttribute('aria-hidden', 'true');
    return h;
  }

  function createGhostColumnCell(row: number, col: number): HTMLDivElement {
    const cell = document.createElement('div');
    cell.className = 'sheet-cell sheet-cell--ghost-column';
    cell.dataset.row = String(row);
    cell.dataset.col = String(col);
    cell.style.width = `${widthOfMountedCol1Based(col)}px`;
    cell.style.left = `${cumulativeColumnWidths[col - 1]}px`;
    cell.style.pointerEvents = 'none';

    const content = document.createElement('div');
    content.className = 'sheet-cell-content';
    content.textContent = '';

    const input = document.createElement('input');
    input.className = 'sheet-cell-input';
    input.type = 'text';
    input.readOnly = true;
    input.tabIndex = -1;
    input.value = '';
    input.setAttribute('aria-hidden', 'true');

    const gr = isGhostRow(row);
    cell.classList.add('sheet-cell-readonly');
    if (gr) cell.classList.add('sheet-cell--ghost');

    cell.append(content, input);
    return cell;
  }

  function setGhostColumnCount(next: number): void {
    if (!ghostColumnsEnabled) return;
    const capped = Math.max(0, Math.min(maxGhostColumns, next));
    if (capped === ghostColumnCount) return;
    const prev = ghostColumnCount;
    ghostColumnCount = capped;
    rebuildCumulativeColumnWidthsAndMountedWidth();

    gridInner.style.width = `${rowHeaderWidthPx + mountedSheetCellsWidth}px`;

    if (capped > prev) {
      for (let g = prev + 1; g <= capped; g++) {
        const col = dataColumnCount + g;
        const h = createGhostColumnHeader(col);
        headerRow.appendChild(h);
        ghostColumnHeaderEls.push(h);
      }
      for (let row = 1; row <= mountedRowCount; row++) {
        const rowCells = rowCellsEls[row - 1]!;
        rowCells.style.width = `${mountedSheetCellsWidth}px`;
        for (let g = prev + 1; g <= capped; g++) {
          const col = dataColumnCount + g;
          const cell = createGhostColumnCell(row, col);
          rowCells.appendChild(cell);
          cells.set(cellKey(row, col), cell);
        }
      }
    } else {
      for (let g = prev; g > capped; g--) {
        ghostColumnHeaderEls.pop()?.remove();
      }
      for (let row = 1; row <= mountedRowCount; row++) {
        const rowCells = rowCellsEls[row - 1]!;
        rowCells.style.width = `${mountedSheetCellsWidth}px`;
        for (let g = prev; g > capped; g--) {
          const col = dataColumnCount + g;
          const k = cellKey(row, col);
          cells.get(k)?.remove();
          cells.delete(k);
        }
      }
    }
    // Padding columns are empty, so they extend how far long text can run.
    scheduleSpillRefreshAll();
  }

  function syncGhostColumnsToViewport(): void {
    if (!ghostColumnsEnabled) return;
    // Grid content is scaled by `zoomFactor`; convert viewport px to content px.
    const vpW = viewport.clientWidth / (zoomFactor || 1);
    const need = Math.ceil((vpW - rowHeaderWidthPx - dataSheetTotalWidth) / ghostColumnWidthPx);
    setGhostColumnCount(need);
  }

  /* --- text overflow (spill across empty neighbours) and wrapping -------- */

  /** Bound on the walk left from a changed cell to re-anchor cells that spill into it. */
  const SPILL_LEFT_WALK_LIMIT = 200;

  /** Coordinates whose stored style asks for wrapped text (fit-checked each pass). */
  const wrapCellKeys = new Set<string>();
  let spillAllScheduled = false;
  let textFitScheduled = false;

  function clearCellSpill(el: HTMLElement): void {
    el.classList.remove('sheet-cell--spill');
    el.style.removeProperty('--sheet-spill-w');
  }

  /** Total width of the visible columns a merge covers. */
  function spanWidthPx(span: CellSpan): number {
    let w = 0;
    for (let c = span.col; c < span.col + span.colSpan && c <= dataColumnCount; c++) {
      if (isHiddenCol(c)) continue;
      w += columnWidths[c - 1] ?? 0;
    }
    return w;
  }

  /** Total height of the rows a merge covers. */
  function spanHeightPx(span: CellSpan): number {
    let h = 0;
    for (let r = span.row; r < span.row + span.rowSpan && r <= dataRowCount; r++) {
      if (r < 1) continue;
      h += rowHeights[r - 1] ?? 0;
    }
    return h;
  }

  /**
   * Does the coordinate hold anything a spill must not run through? Anything
   * stored counts: a value, a style (fill, borders, font), or a comment. Cells
   * inside a merged rectangle also block, because the anchor paints its own box
   * over the coordinates it covers.
   */
  function cellHoldsContent(row: number, col: number): boolean {
    if (row < 1 || row > dataRowCount) return false;
    if (col > dataColumnCount) return false; // ghost padding columns hold nothing
    const stored = data.getStoredCell?.(row, col);
    const raw = stored !== undefined ? stored.value : data.get(row, col);
    const style = stored?.style ?? data.getCellStyle?.(row, col);
    return blocksSpill({
      displayText: cellPlainTextForSearch(columnAt(col), raw),
      hasStyle: Boolean(style && Object.keys(style).length > 0),
      hasComment: Boolean(stored?.comment?.trim()),
      inSpan: coveringSpan(row, col) !== null,
    });
  }

  /** Mounted neighbour widths, walking away from `fromCol` in `delta` steps. */
  function spillNeighbours(row: number, fromCol: number, delta: number): SpillSlot[] {
    const slots: SpillSlot[] = [];
    const lastMounted = dataColumnCount + ghostColumnCount;
    for (let c = fromCol + delta; c >= 1 && c <= lastMounted; c += delta) {
      if (isHiddenCol(c)) continue; // zero layout width, paints nothing
      slots.push({
        widthPx: widthOfMountedCol1Based(c),
        blocks: cellHoldsContent(row, c),
      });
    }
    return slots;
  }

  /**
   * Recompute one cell's spill allowance: when long text has empty mounted columns
   * to run through, widen its content box (`--sheet-spill-w`) so the text paints
   * across those columns and only ellipsizes at the blocker boundary.
   */
  function refreshCellSpill(row: number, col: number): void {
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    if (cellOverflowMode === 'clip') {
      clearCellSpill(el);
      return;
    }
    if (isLinkColumn(columnAt(col))) {
      clearCellSpill(el); // icon buttons size themselves; no spill
      return;
    }
    if (el.classList.contains('sheet-cell--span-covered')) {
      clearCellSpill(el);
      return;
    }
    // Wrapped text that still fits its row stays wrapped; spill is the fallback.
    if (
      el.classList.contains('sheet-cell--wrap') &&
      !el.classList.contains('sheet-cell--wrap-nofit')
    ) {
      clearCellSpill(el);
      return;
    }

    const raw = data.get(row, col);
    const ownText = cellPlainTextForSearch(columnAt(col), raw);
    const span = coveringSpan(row, col);
    const isAnchor = Boolean(span && span.row === row && span.col === col);
    if (!ownText && !isAnchor) {
      clearCellSpill(el);
      return;
    }

    const startCol = isAnchor ? span!.col : col;
    const ownLeft = cumulativeColumnWidths[startCol - 1] ?? 0;
    const ownWidth = isAnchor ? spanWidthPx(span!) : widthOfMountedCol1Based(col);
    const endCol = isAnchor ? Math.min(span!.col + span!.colSpan - 1, dataColumnCount) : col;

    const rightRoom = Math.min(
      spillRoom(spillNeighbours(row, endCol, 1)),
      mountedSheetCellsWidth - (ownLeft + ownWidth),
    );
    const leftRoom = Math.min(spillRoom(spillNeighbours(row, startCol, -1)), ownLeft);
    const inset =
      SHEET_CELL_PAD_X_PX * 2 +
      (isSelectColumn(columnAt(col)) ? SHEET_SELECT_CARET_INSET_PX : 0);
    const direction = spillDirectionFor({
      style: data.getCellStyle?.(row, col),
      numberColumn: columnValueType(columnAt(col)) === 'number',
    });
    const contentWidth = spillContentWidth({
      ownWidthPx: ownWidth,
      direction,
      rightPx: Math.max(0, rightRoom),
      leftPx: Math.max(0, leftRoom),
      contentInsetPx: inset,
    });

    if (contentWidth <= 0 || !spillWidensBox(ownWidth, contentWidth, inset)) {
      clearCellSpill(el);
      return;
    }
    el.style.setProperty('--sheet-spill-w', `${Math.round(contentWidth)}px`);
    el.classList.add('sheet-cell--spill');
  }

  /**
   * Refresh a changed coordinate and the run of cells to its left that could have
   * been spilling into it. Walking stops at the first cell that holds something:
   * cells further left were already clipped by it, so their allowance is unchanged.
   */
  function refreshSpillAround(row: number, col: number): void {
    refreshCellSpill(row, col);
    for (let c = col - 1, steps = 0; c >= 1 && steps < SPILL_LEFT_WALK_LIMIT; c--, steps++) {
      if (isHiddenCol(c)) continue;
      refreshCellSpill(row, c);
      if (cellHoldsContent(row, c)) break;
    }
  }

  function refreshAllSpill(): void {
    for (let row = 1; row <= mountedRowCount; row++) {
      for (let col = 1; col <= dataColumnCount; col++) {
        if (isHiddenCol(col)) continue;
        refreshCellSpill(row, col);
      }
    }
  }

  /**
   * Coalesced full re-measure (column resize, merges, zoom, ghost-column churn).
   * A microtask, not a frame: it batches everything the current gesture changed
   * without deferring past the next paint, and it leaves no timer behind.
   */
  function scheduleSpillRefreshAll(): void {
    if (spillAllScheduled) return;
    spillAllScheduled = true;
    queueMicrotask(() => {
      spillAllScheduled = false;
      refreshAllSpill();
      scheduleTextFitPass();
    });
  }

  /** Wrap on/off straight from the stored `white-space` style key. */
  function applyWrapClass(row: number, col: number): void {
    const el = cells.get(cellKey(row, col));
    if (!el) return;
    const wantsWrap = cellWantsWrap(data.getCellStyle?.(row, col));
    el.classList.toggle('sheet-cell--wrap', wantsWrap);
    const k = cellKey(row, col);
    if (wantsWrap) {
      wrapCellKeys.add(k);
    } else {
      wrapCellKeys.delete(k);
      el.classList.remove('sheet-cell--wrap-nofit');
    }
  }

  /** Vertical room the text has: the row's height, or the whole merge rectangle. */
  function availableTextHeightPx(row: number, col: number): number {
    const span = coveringSpan(row, col);
    const height =
      span && span.row === row && span.col === col
        ? spanHeightPx(span)
        : rowHeights[row - 1] ?? defaultRowHeight;
    return Math.max(0, height - 1);
  }

  /**
   * Wrapping is only allowed while the wrapped lines still fit the row. Anything
   * that needs more height falls back to a single line that spills into empty
   * neighbours (or ellipsizes in place), which is why the fallback re-runs spill.
   */
  function runTextFitPass(): void {
    for (const k of wrapCellKeys) {
      const el = cells.get(k);
      if (!el) {
        wrapCellKeys.delete(k);
        continue;
      }
      const { row, col } = keyToRowCol(k);
      const content = el.querySelector<HTMLElement>('.sheet-cell-content');
      if (!content) continue;
      const fits = content.scrollHeight <= availableTextHeightPx(row, col) + 1;
      el.classList.toggle('sheet-cell--wrap-nofit', !fits);
      refreshCellSpill(row, col);
    }
  }

  function scheduleTextFitPass(): void {
    if (textFitScheduled) return;
    textFitScheduled = true;
    queueMicrotask(runTextFitPass);
  }

  /**
   * Size each merge anchor across its rectangle and hide the covered cells.
   * A horizontal merge is a wider anchor in its row. A vertical merge grows
   * the anchor out of its row; that row is stacked above the rows it covers.
   */
  function layoutCellSpans(): void {
    for (const rowEl of rowEls) rowEl.classList.remove('sheet-row--span-anchor');
    for (let row = 1; row <= dataRowCount; row++) {
      for (let col = 1; col <= dataColumnCount; col++) {
        if (isHiddenCol(col)) continue;
        const cell = cells.get(cellKey(row, col));
        if (!cell) continue;
        cell.classList.remove(
          'sheet-cell--span-anchor',
          'sheet-cell--span-covered',
          'sheet-cell--span-rows',
        );
        cell.style.width = `${columnWidths[col - 1]}px`;
        cell.style.height = '';
        cell.removeAttribute('aria-rowspan');
        cell.removeAttribute('aria-colspan');
        if (!isGhostRow(row)) cell.removeAttribute('aria-hidden');
        applyWrapClass(row, col);
      }
    }
    for (const span of currentSpans()) {
      if (span.row < 1 || span.col < 1) continue;
      if (span.row > dataRowCount || span.col > dataColumnCount) continue;
      if (isHiddenCol(span.col)) continue;
      const anchor = cells.get(cellKey(span.row, span.col));
      if (!anchor) continue;
      let width = 0;
      for (let col = span.col; col < span.col + span.colSpan; col++) {
        if (col > dataColumnCount || isHiddenCol(col)) continue;
        width += columnWidths[col - 1] ?? 0;
      }
      if (width <= 0) continue;
      let height = 0;
      for (let row = span.row; row < span.row + span.rowSpan; row++) {
        if (row < 1 || row > dataRowCount) continue;
        height += rowHeights[row - 1] ?? 0;
      }
      anchor.classList.add('sheet-cell--span-anchor');
      anchor.style.width = `${width}px`;
      if (span.rowSpan > 1) {
        anchor.classList.add('sheet-cell--span-rows');
        anchor.style.height = `${height}px`;
        rowEls[span.row - 1]?.classList.add('sheet-row--span-anchor');
        anchor.setAttribute('aria-rowspan', String(span.rowSpan));
      }
      if (span.colSpan > 1) anchor.setAttribute('aria-colspan', String(span.colSpan));
      for (let row = span.row; row < span.row + span.rowSpan; row++) {
        for (let col = span.col; col < span.col + span.colSpan; col++) {
          if (row === span.row && col === span.col) continue;
          if (row > dataRowCount || col > dataColumnCount || isHiddenCol(col)) continue;
          const covered = cells.get(cellKey(row, col));
          if (!covered) continue;
          covered.classList.add('sheet-cell--span-covered');
          covered.setAttribute('aria-hidden', 'true');
        }
      }
    }
    paintRemotePresenceOverlays();
    scheduleSpillRefreshAll();
  }

  /**
   * Re-apply column layout after `columnWidths[]` mutates. Updates header widths, the cumulative
   * left-offset table, every cell's `width`/`left` (data and ghost), and the grid envelope, then
   * recomputes ghost-column padding. Pass `persist: true` to write to `sessionStorage` once the
   * gesture settles.
   */
  function applyColumnLayout(opts?: { persist?: boolean }): void {
    dataSheetTotalWidth = sumVisibleColumnWidths(columns, columnWidths);
    rebuildCumulativeColumnWidthsAndMountedWidth();
    gridInner.style.width = `${rowHeaderWidthPx + mountedSheetCellsWidth}px`;
    for (let c = 0; c < dataColumnCount; c++) {
      const h = dataColumnHeaderEls[c];
      if (h) h.style.width = `${columnWidths[c]}px`;
    }
    const totalMountedCols = dataColumnCount + ghostColumnCount;
    for (let row = 1; row <= mountedRowCount; row++) {
      const rowCells = rowCellsEls[row - 1];
      if (rowCells) {
        rowCells.style.width = `${mountedSheetCellsWidth}px`;
      }
      for (let col = 1; col <= totalMountedCols; col++) {
        const cell = cells.get(cellKey(row, col));
        if (!cell) continue;
        cell.style.width = `${widthOfMountedCol1Based(col)}px`;
        cell.style.left = `${cumulativeColumnWidths[col - 1]}px`;
      }
    }
    syncGhostColumnsToViewport();
    layoutCellSpans();
    syncCopyMarqueeLayout();
    syncSelectionOutlineLayout();
    if (opts?.persist) {
      persistColumnWidths(columnWidthPersistenceKey, columnWidths);
    }
  }

  layoutCellSpans();
  refreshAllSpill();

  /* --- value bar: single-line editor for the active cell ----------------- */

  if (config.showValueBar !== false) {
    valueBar = createCellInputBar({
      root,
      onInput: (text) => {
        const key = cellKey(active.row, active.col);
        if (isGhostRow(active.row) || !isEditableCol(active.col)) {
          // The field is read-only; guard anyway because autofill can bypass it.
          const raw = data.get(active.row, active.col);
          valueBar?.setValue(raw !== undefined ? String(raw) : '');
          return;
        }
        barDirtyCoord = key;
        const cellEl = cells.get(key);
        const inp = cellEl ? getCellEditor(cellEl) : null;
        if (inp && inp.value !== text) inp.value = text;
        valueBar?.setStatus(null);
        valueBar?.setPending(true);
      },
      onCommitMove: (move) => commitValueBar(move),
      onCommit: () => commitValueBar(null),
      onRevert: () => revertValueBar(),
      onFocusIn: () => valueBar?.setStatus(null),
      onFocusOut: () => {},
    });
    // The active coordinate is seeded before listeners can fire, so paint the bar once.
    syncValueBar();
  }

  /**
   * Text staged in the cell's editor — in-cell edit mode, or mirrored from the
   * value bar — or `null` when nothing is pending for that coordinate.
   */
  function pendingEditText(row: number, col: number): string | null {
    if (row !== active.row || col !== active.col) return null;
    const key = cellKey(row, col);
    if (!editMode && barDirtyCoord !== key) return null;
    const el = cells.get(key);
    const inp = el ? getCellEditor(el) : null;
    return inp ? inp.value : null;
  }

  /** Reflect the active coordinate in the bar: label, text, editability, pending state. */
  function syncValueBar(): void {
    if (!valueBar) return;
    const row = active.row;
    const col = active.col;
    const rect = currentSelectionRect();
    const multi = rect.rowSpan > 1 || rect.colSpan > 1;
    const header = columnAt(col)?.header?.trim() ?? '';
    valueBar.setLabel(
      cellRangeLabel(rect),
      multi ? `${header} · ${rect.rowSpan * rect.colSpan} cells` : header,
      multi,
    );

    const pending = pendingEditText(row, col);
    if (pending !== null) {
      valueBar.setValue(pending);
    } else {
      const raw = isGhostRow(row) ? undefined : data.get(row, col);
      valueBar.setValue(raw !== undefined ? String(raw) : '');
    }
    valueBar.setEditable(!isGhostRow(row) && isEditableCol(col));
    valueBar.setPending(editMode || barDirtyCoord === cellKey(row, col));
  }

  /** Drop whatever is staged and go back to the stored value. */
  function revertValueBar(): void {
    const key = cellKey(active.row, active.col);
    barDirtyCoord = null;
    const cellEl = cells.get(key);
    const inp = cellEl ? getCellEditor(cellEl) : null;
    const raw = data.get(active.row, active.col);
    const stored = raw !== undefined ? String(raw) : '';
    if (inp) inp.value = stored;
    valueBar?.setValue(stored);
    valueBar?.setStatus(null);
    valueBar?.setPending(false);
  }

  /**
   * Commit the bar's text, then move the selection the way the grid's Enter/Tab
   * would (`move` is null for a plain commit, e.g. blur). A value the column
   * rejects does not move: the bar explains it inline instead.
   */
  function commitValueBar(move: ValueBarMove | null): void {
    if (!valueBar) return;
    const row = active.row;
    const col = active.col;
    const key = cellKey(row, col);

    if (barDirtyCoord === key) {
      const cellEl = cells.get(key);
      const inp = cellEl ? getCellEditor(cellEl) : null;
      if (inp && !editMode) inp.value = valueBar.getValue();
      persistCellEdit(row, col);
      if (barDirtyCoord === key) {
        // Still staged => the column rejected it. Keep the coordinate, say why.
        const raw = data.get(row, col);
        valueBar.setStatus(rejectionMessageFor(columnAt(col)));
        valueBar.setValue(raw !== undefined ? String(raw) : '');
        valueBar.setPending(false);
        return;
      }
    }

    if (move === null) {
      syncValueBar();
      return;
    }
    if (move === 'down' || move === 'up') {
      const targetCol = clampColVisible(tabAnchorCol ?? col);
      tabAnchorCol = null;
      const next = cellAfterStep(row, targetCol, move === 'down' ? 1 : -1, 0);
      setActive(next.row, next.col);
    } else {
      if (tabAnchorCol === null) tabAnchorCol = col;
      const next = cellAfterStep(row, col, 0, move === 'right' ? 1 : -1);
      setActive(next.row, next.col);
    }
    valueBar.blur();
    syncValueBar();
  }

  function setColumnWidth(colIndex: number, nextWidth: number, opts?: { persist?: boolean }): void {
    if (colIndex < 1 || colIndex > dataColumnCount) return;
    const clamped = clampColumnWidth(nextWidth);
    if (columnWidths[colIndex - 1] === clamped) return;
    columnWidths[colIndex - 1] = clamped;
    applyColumnLayout({ persist: opts?.persist === true });
  }

  /** Fit a column's width to the widest rendered content (header + data cells). */
  function autofitColumn(col: number): void {
    if (col < 1 || col > dataColumnCount || isHiddenCol(col)) return;
    let max = 0;
    const headerLabel = dataColumnHeaderEls[col - 1]?.querySelector<HTMLElement>(
      '.sheet-column-header-label',
    );
    if (headerLabel) max = Math.max(max, headerLabel.scrollWidth);
    for (let row = 1; row <= dataRowCount; row++) {
      const cell = cells.get(cellKey(row, col));
      if (!cell) continue;
      if (
        cell.classList.contains('sheet-cell--span-anchor') ||
        cell.classList.contains('sheet-cell--span-covered')
      ) {
        continue;
      }
      const content = cell.querySelector<HTMLElement>('.sheet-cell-content');
      if (content) max = Math.max(max, content.scrollWidth);
    }
    const padding = isSelectColumn(columnAt(col)) ? 44 : 28;
    setColumnWidth(col, max + padding, { persist: true });
  }

  if (columnResizeEnabled) {
    let resizeCol = 0;
    let resizeStartX = 0;
    let resizeStartWidth = 0;
    let activeGrip: HTMLElement | null = null;
    let resizePointerId: number | null = null;
    let resizeRaf: number | null = null;
    let resizePendingWidth: number | null = null;

    const flushPendingResize = (): void => {
      resizeRaf = null;
      if (resizePendingWidth === null || resizeCol === 0) return;
      const desired = resizePendingWidth;
      resizePendingWidth = null;
      setColumnWidth(resizeCol, desired, { persist: false });
    };

    const onGripPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      const grip = e.currentTarget as HTMLElement;
      const col = Number(grip.dataset.col);
      if (!Number.isFinite(col) || col < 1 || col > dataColumnCount) return;
      e.preventDefault();
      e.stopPropagation();
      resizeCol = col;
      resizeStartX = e.clientX;
      resizeStartWidth = columnWidths[col - 1]!;
      resizePointerId = e.pointerId;
      activeGrip = grip;
      grip.classList.add('sheet-column-resize-handle--dragging');
      grip.setPointerCapture(e.pointerId);
    };

    const onGripPointerMove = (e: PointerEvent): void => {
      if (resizePointerId === null || e.pointerId !== resizePointerId) return;
      const next = resizeStartWidth + (e.clientX - resizeStartX) / (zoomFactor || 1);
      resizePendingWidth = next;
      if (resizeRaf === null) {
        resizeRaf = requestAnimationFrame(flushPendingResize);
      }
    };

    const onGripPointerUp = (e: PointerEvent): void => {
      if (resizePointerId === null || e.pointerId !== resizePointerId) return;
      if (resizeRaf !== null) {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = null;
      }
      flushPendingResize();
      if (activeGrip) {
        activeGrip.classList.remove('sheet-column-resize-handle--dragging');
        if (activeGrip.hasPointerCapture(e.pointerId)) {
          activeGrip.releasePointerCapture(e.pointerId);
        }
      }
      resizePointerId = null;
      activeGrip = null;
      resizeCol = 0;
      resizePendingWidth = null;
      persistColumnWidths(columnWidthPersistenceKey, columnWidths);
    };

    const onGripDblClick = (e: MouseEvent): void => {
      const grip = e.currentTarget as HTMLElement;
      const col = Number(grip.dataset.col);
      if (!Number.isFinite(col) || col < 1 || col > dataColumnCount) return;
      e.preventDefault();
      e.stopPropagation();
      autofitColumn(col);
    };

    for (const h of dataColumnHeaderEls) {
      if (!h) continue;
      const grip = h.querySelector<HTMLElement>('.sheet-column-resize-handle');
      if (!grip) continue;
      grip.addEventListener('pointerdown', onGripPointerDown);
      grip.addEventListener('pointermove', onGripPointerMove);
      grip.addEventListener('pointerup', onGripPointerUp);
      grip.addEventListener('pointercancel', onGripPointerUp);
      grip.addEventListener('dblclick', onGripDblClick);
    }
  }

  const suggestPopover = document.createElement('div');
  suggestPopover.className = 'sheet-suggest-popover';
  suggestPopover.hidden = true;
  suggestPopover.setAttribute('role', 'listbox');
  const suggestScroll = document.createElement('div');
  suggestScroll.className = 'sheet-suggest-scroll';
  suggestPopover.appendChild(suggestScroll);

  let suggestionMatches: SpreadsheetSelectOption[] = [];
  let suggestionHighlight = -1;
  let blurHideTimer: ReturnType<typeof setTimeout> | null = null;

  function hideSuggestions(): void {
    if (blurHideTimer !== null) {
      clearTimeout(blurHideTimer);
      blurHideTimer = null;
    }
    suggestionHighlight = -1;
    suggestionMatches = [];
    suggestScroll.replaceChildren();
    suggestPopover.hidden = true;
    suggestPopover.classList.remove('sheet-suggest-popover--open');
  }

  function positionSuggestPopover(): void {
    const cell = cells.get(cellKey(active.row, active.col));
    if (!cell) return;
    const r = cell.getBoundingClientRect();
    suggestPopover.style.top = `${r.bottom + 2}px`;
    suggestPopover.style.left = `${r.left}px`;
    suggestPopover.style.minWidth = `${Math.max(r.width, 100)}px`;
  }

  function updateSuggestionHighlightClass(): void {
    const rows = suggestScroll.querySelectorAll('.sheet-suggest-row');
    rows.forEach((row, i) => {
      row.classList.toggle('sheet-suggest-row--active', i === suggestionHighlight);
      if (i === suggestionHighlight) {
        row.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  function renderSuggestionRows(matches: SpreadsheetSelectOption[]): void {
    suggestScroll.replaceChildren();
    suggestionMatches = matches;
    for (let i = 0; i < matches.length; i++) {
      const row = document.createElement('div');
      row.className = 'sheet-suggest-row';
      row.setAttribute('role', 'option');
      row.dataset.index = String(i);
      const opt = matches[i]!;
      row.innerHTML = formatSelectOptionMarkup(opt, opt.label ?? opt.value);
      row.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        if (blurHideTimer !== null) {
          clearTimeout(blurHideTimer);
          blurHideTimer = null;
        }
        applySuggestionByIndex(i);
      });
      suggestScroll.appendChild(row);
    }
    updateSuggestionHighlightClass();
  }

  function showSuggestionsFromQuery(query: string): void {
    const col = columnAt(active.col);
    if (!isSelectColumn(col) || isReadOnlyCol(active.col)) {
      hideSuggestions();
      return;
    }
    const matches = filterSelectOptions(col, query);
    if (matches.length === 0) {
      hideSuggestions();
      return;
    }
    renderSuggestionRows(matches);
    positionSuggestPopover();
    suggestPopover.hidden = false;
    suggestPopover.classList.add('sheet-suggest-popover--open');
    updateSuggestionHighlightClass();
  }

  function applySuggestionByIndex(i: number): void {
    if (i < 0 || i >= suggestionMatches.length) return;
    const opt = suggestionMatches[i]!;
    const cell = cells.get(cellKey(active.row, active.col));
    const input = cell ? getCellEditor(cell) : null;
    if (!input) return;
    input.value = opt.value;
    hideSuggestions();
    input.focus();
  }

  function openFullSuggestionList(): void {
    const col = columnAt(active.col);
    if (!isSelectColumn(col) || isReadOnlyCol(active.col)) return;
    showSuggestionsFromQuery('');
    suggestionHighlight = suggestionMatches.length > 0 ? 0 : -1;
    updateSuggestionHighlightClass();
  }

  function suggestionKeysConsumed(e: KeyboardEvent): boolean {
    const ae = document.activeElement;
    if (!(ae instanceof HTMLInputElement) || ae.dataset.sheetValueType !== 'select') {
      return false;
    }
    const cellEl = ae.closest('.sheet-cell');
    if (!cellEl || !viewport.contains(cellEl)) return false;
    const r = clampRow(Number((cellEl as HTMLElement).dataset.row));
    const c = clampCol(Number((cellEl as HTMLElement).dataset.col));
    if (r !== active.row || c !== active.col) return false;

    const sheetMod = e.metaKey || e.ctrlKey;

    if (e.altKey && e.key === 'ArrowDown' && !sheetMod && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      openFullSuggestionList();
      return true;
    }

    if (suggestPopover.hidden) return false;

    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      hideSuggestions();
      return true;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      if (suggestionMatches.length > 0) {
        const i = suggestionHighlight >= 0 ? suggestionHighlight : 0;
        applySuggestionByIndex(i);
      }
      return true;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (suggestionHighlight >= 0) {
        applySuggestionByIndex(suggestionHighlight);
      } else {
        hideSuggestions();
      }
      setActive(active.row + 1, active.col);
      return true;
    }

    if (e.key === 'ArrowDown' && !sheetMod && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (suggestionMatches.length === 0) return true;
      if (suggestionHighlight < suggestionMatches.length - 1) suggestionHighlight++;
      else suggestionHighlight = 0;
      updateSuggestionHighlightClass();
      return true;
    }

    if (e.key === 'ArrowUp' && !sheetMod && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (suggestionMatches.length === 0) return true;
      if (suggestionHighlight > 0) suggestionHighlight--;
      else suggestionHighlight = suggestionMatches.length - 1;
      updateSuggestionHighlightClass();
      return true;
    }

    return false;
  }

  commentPopover = document.createElement('div');
  commentPopover.className = 'sheet-comment-popover';
  commentPopover.hidden = true;
  commentPopover.setAttribute('role', 'dialog');
  commentPopover.setAttribute('aria-label', 'Cell comment');

  const commentHead = document.createElement('div');
  commentHead.className = 'sheet-comment-popover__head';
  commentHead.textContent = 'Comment';

  commentTextarea = document.createElement('textarea');
  commentTextarea.className = 'sheet-comment-popover__input';
  commentTextarea.rows = 4;
  commentTextarea.setAttribute('aria-multiline', 'true');

  const commentActions = document.createElement('div');
  commentActions.className = 'sheet-comment-popover__actions';

  const btnCommentRemove = document.createElement('button');
  btnCommentRemove.type = 'button';
  btnCommentRemove.className = 'sheet-comment-popover__btn sheet-comment-popover__btn--muted';
  btnCommentRemove.textContent = 'Remove';

  const btnCommentCancel = document.createElement('button');
  btnCommentCancel.type = 'button';
  btnCommentCancel.className = 'sheet-comment-popover__btn sheet-comment-popover__btn--muted';
  btnCommentCancel.textContent = 'Cancel';

  const btnCommentSave = document.createElement('button');
  btnCommentSave.type = 'button';
  btnCommentSave.className = 'sheet-comment-popover__btn sheet-comment-popover__btn--primary';
  btnCommentSave.textContent = 'Save';

  btnCommentRemove.addEventListener('click', () => {
    const anchor = commentEditorAnchor;
    if (!anchor) return;
    commitCommentAt(anchor.row, anchor.col, '');
    if (commentTextarea) commentTextarea.value = '';
    if (commentPopover) commentPopover.hidden = true;
    commentEditorAnchor = null;
    viewport.focus();
  });

  btnCommentCancel.addEventListener('click', () => closeCommentEditor(false));
  btnCommentSave.addEventListener('click', () => closeCommentEditor(true));

  commentTextarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeCommentEditor(false);
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      closeCommentEditor(true);
    }
  });

  commentActions.append(btnCommentRemove, btnCommentCancel, btnCommentSave);
  commentPopover.append(commentHead, commentTextarea, commentActions);
  commentPopover.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeCommentEditor(false);
    }
  });

  gridInner.append(headerRow, gridBody);
  viewport.append(gridInner);
  root.append(viewport, suggestPopover, commentPopover, commentPreviewPop, srAnnounceEl, inputHintEl);
  container.appendChild(root);

  let disconnectLayout: (() => void) | undefined;
  // Webfont metrics decide both the wrapped line count and how far text reaches,
  // so re-measure once the sheet's font has actually loaded.
  const docFonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
  if (docFonts?.ready && typeof docFonts.ready.then === 'function') {
    docFonts.ready
      .then(() => {
        scheduleSpillRefreshAll();
      })
      .catch(() => {});
  }

  if (ghostColumnsEnabled) {
    const ro = new ResizeObserver(() => {
      syncGhostColumnsToViewport();
    });
    ro.observe(viewport);
    queueMicrotask(() => syncGhostColumnsToViewport());
    disconnectLayout = () => ro.disconnect();
  }

  {
    const previousDisconnect = disconnectLayout;
    disconnectLayout = () => {
      imageLayer?.destroy();
      imageLayer = null;
      previousDisconnect?.();
    };
  }

  commentPreviewPop.addEventListener('pointerenter', () => {
    if (commentPreviewHideTimer !== null) {
      clearTimeout(commentPreviewHideTimer);
      commentPreviewHideTimer = null;
    }
  });
  commentPreviewPop.addEventListener('pointerleave', (e) => {
    const rel = e.relatedTarget as Node | null;
    if (rel && commentPreviewSourceHit && (rel === commentPreviewSourceHit || commentPreviewSourceHit.contains(rel))) {
      return;
    }
    scheduleHideCommentPreview();
  });

  viewport.addEventListener(
    'pointerover',
    (e) => {
      const hit = (e.target as HTMLElement).closest?.('.sheet-cell-comment-hit');
      if (!hit || !viewport.contains(hit)) return;
      showCommentPreviewFromHit(hit as HTMLButtonElement);
    },
    true,
  );

  viewport.addEventListener(
    'pointerout',
    (e) => {
      const hit = (e.target as HTMLElement).closest?.('.sheet-cell-comment-hit');
      if (!hit || !viewport.contains(hit)) return;
      const rel = e.relatedTarget as Node | null;
      if (rel && (hit.contains(rel) || commentPreviewPop.contains(rel))) return;
      scheduleHideCommentPreview();
    },
    true,
  );

  viewport.addEventListener(
    'click',
    (e) => {
      const hit = (e.target as HTMLElement).closest?.('.sheet-cell-comment-hit') as
        | HTMLButtonElement
        | null;
      if (!hit || !viewport.contains(hit)) return;
      if (!commentsEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      const row = clampRow(Number(hit.dataset.row));
      const col = clampCol(Number(hit.dataset.col));
      hideCommentPreview();
      setActive(row, col);
      openCommentEditor();
    },
    true,
  );

  viewport.addEventListener('copy', onCopyCapture, true);
  viewport.addEventListener('paste', onPasteCapture, true);
  viewport.addEventListener('cut', onCutCapture, true);

  // Reveal the full value as a native tooltip only when the cell text is clipped.
  viewport.addEventListener('pointerover', (e) => {
    const cellEl = (e.target as HTMLElement).closest?.('.sheet-cell') as HTMLElement | null;
    if (!cellEl || !viewport.contains(cellEl)) return;
    if (cellEl.classList.contains('sheet-cell--ghost-column')) return;
    const content = cellEl.querySelector<HTMLElement>('.sheet-cell-content');
    if (!content) {
      cellEl.removeAttribute('title');
      return;
    }
    const clipped = content.scrollWidth > content.clientWidth + 1;
    if (!clipped) {
      if (cellEl.hasAttribute('title')) cellEl.removeAttribute('title');
      return;
    }
    const r = Number(cellEl.dataset.row);
    const c = Number(cellEl.dataset.col);
    const text = isGhostRow(r) ? '' : cellPlainTextForSearch(columnAt(c), data.get(r, c));
    if (text) {
      if (cellEl.getAttribute('title') !== text) cellEl.setAttribute('title', text);
    } else if (cellEl.hasAttribute('title')) {
      cellEl.removeAttribute('title');
    }
  });

  viewport.addEventListener('scroll', () => {
    hideInputHint();
    if (!suggestPopover.hidden) positionSuggestPopover();
    if (commentPopover && !commentPopover.hidden && commentEditorAnchor) {
      positionCommentPopover(commentEditorAnchor.row, commentEditorAnchor.col);
    }
    if (!commentPreviewPop.hidden && commentPreviewSourceHit) {
      positionCommentPreview(commentPreviewSourceHit);
    }
  });

  // Long text typed in-cell shows up in the value bar as it is typed.
  viewport.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || !t.classList.contains('sheet-cell-input')) return;
    if (valueBar && !valueBar.hasFocus()) valueBar.setValue(t.value);
    valueBar?.setPending(true);
  });

  viewport.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.dataset.sheetValueType !== 'select') return;
    const cellEl = t.closest('.sheet-cell');
    if (!cellEl) return;
    const row = clampRow(Number((cellEl as HTMLElement).dataset.row));
    const col = clampCol(Number((cellEl as HTMLElement).dataset.col));
    if (row !== active.row || col !== active.col) return;
    suggestionHighlight = -1;
    showSuggestionsFromQuery(t.value);
  });

  viewport.addEventListener('focusin', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.dataset.sheetValueType !== 'select') return;
    const cellEl = t.closest('.sheet-cell');
    if (!cellEl) return;
    const row = clampRow(Number((cellEl as HTMLElement).dataset.row));
    const col = clampCol(Number((cellEl as HTMLElement).dataset.col));
    if (row !== active.row || col !== active.col) return;
    showSuggestionsFromQuery(t.value);
  });

  viewport.addEventListener('focusout', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.dataset.sheetValueType !== 'select') return;
    const rt = e.relatedTarget as Node | null;
    if (rt && suggestPopover.contains(rt)) return;
    if (rt && commentPopover?.contains(rt)) return;
    if (rt && commentPreviewPop.contains(rt)) return;
    blurHideTimer = setTimeout(() => hideSuggestions(), 120);
  });

  function cellFromPoint(clientX: number, clientY: number): { row: number; col: number } | null {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const hit = el?.closest('.sheet-cell');
    if (!hit || !viewport.contains(hit)) return null;
    const cell = hit as HTMLElement;
    const row = Number(cell.dataset.row);
    const col = Number(cell.dataset.col);
    if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
    return { row: clampRow(row), col: clampCol(col) };
  }

  function setEditingOnActive(on: boolean): void {
    cells.get(cellKey(active.row, active.col))?.classList.toggle('sheet-cell-editing', on);
  }

  function focusActiveCell(): void {
    if (editMode) return;
    setTimeout(() => viewport.focus({ preventScroll: true }), 0);
  }

  function enterNavigationMode(): void {
    setEditingOnActive(false);
    editMode = false;
    const cell = cells.get(cellKey(active.row, active.col));
    const inp = cell ? getCellEditor(cell) : null;
    if (inp && document.activeElement === inp) inp.blur();
    viewport.focus({ preventScroll: true });
  }

  function enterEditMode(opts?: { seed?: string }): void {
    if (!isEditableCol(active.col) || isGhostRow(active.row)) return;
    const cell = cells.get(cellKey(active.row, active.col));
    const inp = cell ? getCellEditor(cell) : null;
    if (!inp || !cell) return;
    cell.classList.add('sheet-cell-editing');
    if (opts && opts.seed !== undefined) {
      inp.value = opts.seed;
    } else {
      const stored = data.get(active.row, active.col);
      inp.value = stored !== undefined ? String(stored) : '';
    }
    editMode = true;
    inp.focus();
    try {
      const len = inp.value.length;
      inp.setSelectionRange(len, len);
    } catch {
      /* setSelectionRange may throw for non-text inputs; safe to ignore */
    }
    syncValueBar();
  }

  function blurActiveInput(): void {
    const cell = cells.get(cellKey(active.row, active.col));
    if (cell) getCellEditor(cell)?.blur();
  }

  function leaveEditorFocusSheet(): void {
    blurActiveInput();
    viewport.focus({ preventScroll: true });
  }

  let lastSelectKeys = new Set<string>();
  let rangeConsumedEditorFocus = false;

  function exitRangeSelectionUi(): void {
    rangeConsumedEditorFocus = false;
  }

  function enterRangeSelectionUi(): void {
    if (!rangeConsumedEditorFocus) {
      leaveEditorFocusSheet();
      rangeConsumedEditorFocus = true;
    }
  }

  function expandActiveSelectionToSpans(): void {
    if (!selectArea.active) return;
    const rect = currentSelectionRect();
    const expanded = expandRectToSpans(rect, currentSpans());
    if (
      expanded.row === rect.row &&
      expanded.col === rect.col &&
      expanded.rowSpan === rect.rowSpan &&
      expanded.colSpan === rect.colSpan
    ) {
      return;
    }
    const anchorIsMinRow = selectArea.row <= selectArea.rowEnd;
    const anchorIsMinCol = selectArea.col <= selectArea.colEnd;
    const maxRow = expanded.row + expanded.rowSpan - 1;
    const maxCol = expanded.col + expanded.colSpan - 1;
    selectArea.row = anchorIsMinRow ? expanded.row : maxRow;
    selectArea.rowEnd = anchorIsMinRow ? maxRow : expanded.row;
    selectArea.col = anchorIsMinCol ? expanded.col : maxCol;
    selectArea.colEnd = anchorIsMinCol ? maxCol : expanded.col;
  }

  function syncSelectionHighlight(): void {
    expandActiveSelectionToSpans();
    const nextKeys = new Set<string>();
    if (selectArea.active) {
      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      for (let row = minRow; row <= maxRow; row++) {
        for (let col = minCol; col <= maxCol; col++) {
          nextKeys.add(cellKey(row, col));
        }
      }
    }
    for (const key of lastSelectKeys) {
      if (!nextKeys.has(key)) {
        cells.get(key)?.classList.remove('sheet-cell-select-area');
      }
    }
    for (const key of nextKeys) {
      if (!lastSelectKeys.has(key)) {
        cells.get(key)?.classList.add('sheet-cell-select-area');
      }
    }
    lastSelectKeys = nextKeys;
    syncSelectionOutlineLayout();
    notifySelectionChange();
  }

  function flushDragPaint(): void {
    if (dragRaf !== null) {
      cancelAnimationFrame(dragRaf);
      dragRaf = null;
    }
    if (dragPendingEnd !== null) {
      const p = dragPendingEnd;
      dragPendingEnd = null;
      applyDragRange(p.row, p.col);
    }
  }

  function scheduleDragPaint(): void {
    if (dragRaf !== null) return;
    dragRaf = requestAnimationFrame(() => {
      dragRaf = null;
      if (!dragging) return;
      const p = dragPendingEnd;
      if (p === null) return;
      dragPendingEnd = null;
      applyDragRange(p.row, p.col);
      if (dragPendingEnd !== null) scheduleDragPaint();
    });
  }

  function collapseRange(): void {
    selectArea.row = active.row;
    selectArea.col = active.col;
    selectArea.rowEnd = active.row;
    selectArea.colEnd = active.col;
    selectArea.active = false;
    exitRangeSelectionUi();
    syncSelectionHighlight();
  }

  function setActive(row: number, col: number): void {
    const covering = coveringSpan(row, col);
    if (covering) {
      row = covering.row;
      col = covering.col;
    }
    row = clampRow(row);
    col = clampColVisible(col);

    if (active.row === row && active.col === col && !selectArea.active) {
      queueMicrotask(() => {
        viewport.focus({ preventScroll: true });
        scrollActiveIntoView();
      });
      return;
    }

    hideSuggestions();
    hideInputHint();
    exitRangeSelectionUi();
    selectArea.active = false;
    selectArea.row = row;
    selectArea.col = col;
    selectArea.rowEnd = row;
    selectArea.colEnd = col;
    syncSelectionHighlight();

    persistActiveInput();

    const prev = cells.get(cellKey(active.row, active.col));
    prev?.classList.remove('sheet-cell-active', 'sheet-cell-editing');
    prev?.removeAttribute('aria-selected');

    active = { row, col };
    editMode = false;
    // Whatever was staged for the previous coordinate has already been committed
    // (or reverted) above; a stale flag would stage it again on the next commit.
    if (barDirtyCoord && barDirtyCoord !== cellKey(row, col)) barDirtyCoord = null;
    const next = cells.get(cellKey(row, col));
    next?.classList.add('sheet-cell-active');
    next?.setAttribute('aria-selected', 'true');

    const inp = next ? getCellEditor(next) : null;
    if (inp) {
      const stored = data.get(row, col);
      inp.value = stored !== undefined ? String(stored) : '';
    }
    // The selection notify above still describes the previous coordinate, so the
    // bar needs an explicit sync once `active` and its editor buffer are updated.
    syncValueBar();
    announceActiveCell();
    focusActiveCell();
    scrollActiveIntoView();
  }

  function scrollActiveIntoView(): void {
    const cell = cells.get(cellKey(active.row, active.col));
    if (!cell) return;
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  function announceActiveCell(): void {
    if (!srAnnounceEl) return;
    if (isGhostRow(active.row)) return;
    const colDef = columnAt(active.col);
    const header = colDef?.header?.trim() || `Column ${active.col}`;
    const text = cellPlainTextForSearch(colDef, data.get(active.row, active.col));
    srAnnounceEl.textContent = `${header}, row ${active.row}: ${text || 'empty'}`;
  }

  function applyDragRange(endRow: number, endCol: number): void {
    endRow = clampRow(endRow);
    endCol = clampCol(endCol);
    selectArea.row = dragAnchor.row;
    selectArea.col = dragAnchor.col;
    selectArea.rowEnd = endRow;
    selectArea.colEnd = endCol;
    const multi =
      selectArea.row !== selectArea.rowEnd || selectArea.col !== selectArea.colEnd;
    selectArea.active = multi;
    syncSelectionHighlight();
    if (multi) enterRangeSelectionUi();
  }

  function selectSheetRow(targetRow: number, extend = false): void {
    const row = clampRow(targetRow);
    persistActiveInput();
    hideSuggestions();
    tabAnchorCol = null;
    editMode = false;
    cells.get(cellKey(active.row, active.col))?.classList.remove('sheet-cell-active', 'sheet-cell-editing');
    const anchorRow = extend && selectArea.active ? selectArea.row : row;
    active = { row, col: firstVisibleCol() };
    const next = cells.get(cellKey(row, firstVisibleCol()));
    next?.classList.add('sheet-cell-active');
    const inp = next ? getCellEditor(next) : null;
    if (inp) {
      const stored = data.get(row, firstVisibleCol());
      inp.value = stored !== undefined ? String(stored) : '';
    }
    selectArea.row = anchorRow;
    selectArea.col = firstVisibleCol();
    selectArea.rowEnd = row;
    selectArea.colEnd = lastVisibleCol();
    selectArea.active =
      dataRowCount > 1 ||
      firstVisibleCol() !== lastVisibleCol() ||
      anchorRow !== row;
    syncSelectionHighlight();
    if (selectArea.active) {
      enterRangeSelectionUi();
    } else {
      exitRangeSelectionUi();
      focusActiveCell();
    }
    notifySelectionChange();
  }

  function selectSheetColumn(targetCol: number, extend = false): void {
    const col = clampCol(targetCol);
    if (dataRowCount < 1) return;
    persistActiveInput();
    hideSuggestions();
    tabAnchorCol = null;
    editMode = false;
    cells.get(cellKey(active.row, active.col))?.classList.remove('sheet-cell-active', 'sheet-cell-editing');
    const anchorCol = extend && selectArea.active ? selectArea.col : col;
    active = { row: 1, col };
    const next = cells.get(cellKey(1, col));
    next?.classList.add('sheet-cell-active');
    const inp = next ? getCellEditor(next) : null;
    if (inp) {
      const stored = data.get(1, col);
      inp.value = stored !== undefined ? String(stored) : '';
    }
    selectArea.row = 1;
    selectArea.col = anchorCol;
    selectArea.rowEnd = dataRowCount;
    selectArea.colEnd = col;
    selectArea.active = dataRowCount > 1 || anchorCol !== col;
    syncSelectionHighlight();
    if (selectArea.active) {
      enterRangeSelectionUi();
    } else {
      exitRangeSelectionUi();
      focusActiveCell();
    }
    notifySelectionChange();
  }

  createRowContextMenu({
    root,
    viewport,
    dataRowCount,
    clampRow,
    onClearRow: (row) => clearSheetRow(row),
    onSelectRow: (row) => selectSheetRow(row),
    onCopyRow: (row) => {
      persistActiveInput();
      writeClipboardText(buildRowPlainText(row));
      setCopyMarquee({
        minRow: row,
        maxRow: row,
        minCol: firstVisibleCol(),
        maxCol: lastVisibleCol(),
      });
    },
  });

  function endPointerDrag(focusIfClick: boolean): void {
    viewport.classList.remove('sheet-viewport--dragging');
    dragging = false;
    dragPointerId = null;

    if (!dragMoved) {
      collapseRange();
      if (focusIfClick) focusActiveCell();
    } else if (selectArea.active) {
      /* focus already moved to viewport on first multi frame */
    } else {
      focusActiveCell();
    }
  }

  viewport.addEventListener('click', (e) => {
    const linkHit = (e.target as HTMLElement | null)?.closest?.('a.sheet-cell-link');
    if (!linkHit || !viewport.contains(linkHit)) return;
    if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey || e.button !== 0) return;
    const linkCell = linkHit.closest('.sheet-cell') as HTMLElement | null;
    if (!linkCell) return;
    const cr = clampRow(Number(linkCell.dataset.row));
    const cc = clampCol(Number(linkCell.dataset.col));
    if (isGhostRow(cr)) return;
    // After the browser follows the link, so focusing the grid does not cancel it.
    setTimeout(() => {
      if (cr === active.row && cc === active.col) return;
      setActive(cr, cc);
    }, 0);
  });

  viewport.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('.sheet-floating-image')) return;
    imageLayer?.selectNone();

    // Leave the click intact so the browser follows the URL. Selecting here
    // would focus the grid during pointerdown and cancel that navigation.
    const linkHit = target.closest('a.sheet-cell-link');
    if (linkHit && viewport.contains(linkHit)) return;

    const caretHit = target.closest('.sheet-cell-select-caret') as HTMLElement | null;
    if (caretHit && viewport.contains(caretHit)) {
      const caretCell = caretHit.closest('.sheet-cell') as HTMLElement | null;
      if (caretCell) {
        const cr = clampRow(Number(caretCell.dataset.row));
        const cc = clampCol(Number(caretCell.dataset.col));
        e.preventDefault();
        if (cr !== active.row || cc !== active.col) setActive(cr, cc);
        if (!isReadOnlyCol(cc) && !isGhostRow(cr) && isSelectColumn(columnAt(cc))) {
          enterEditMode();
          openFullSuggestionList();
        }
        return;
      }
    }

    const headerHit = target.closest('.sheet-column-header') as HTMLElement | null;
    if (headerHit && viewport.contains(headerHit) && !headerHit.classList.contains('sheet-column-header--ghost')) {
      const col = Number(headerHit.dataset.col);
      if (!Number.isFinite(col) || col < 1 || col > dataColumnCount) return;
      e.preventDefault();
      selectSheetColumn(col, e.shiftKey);
      return;
    }

    const gutterHit = target.closest('.sheet-row-gutter') as HTMLElement | null;
    if (gutterHit && viewport.contains(gutterHit) && !gutterHit.classList.contains('sheet-row-gutter--ghost')) {
      const gRow = Number(gutterHit.dataset.row);
      if (!Number.isFinite(gRow) || gRow < 1 || gRow > dataRowCount) return;
      e.preventDefault();
      selectSheetRow(gRow, e.shiftKey);
      return;
    }

    const hit = target.closest('.sheet-cell');
    if (!hit || !viewport.contains(hit)) return;
    const cell = hit as HTMLElement;
    let row = Number(cell.dataset.row);
    let col = Number(cell.dataset.col);
    if (!Number.isFinite(row) || !Number.isFinite(col)) return;
    row = clampRow(row);
    col = clampCol(col);

    e.preventDefault();
    if (dragRaf !== null) {
      cancelAnimationFrame(dragRaf);
      dragRaf = null;
    }
    dragPendingEnd = null;

    // Shift-click extends the existing range from the current anchor instead
    // of starting a fresh drag. The anchor is `selectArea.row/col` when a
    // range is already active, otherwise the current `active` cell.
    if (e.shiftKey) {
      hideSuggestions();
      persistActiveInput();
      const anchorRow = selectArea.active ? selectArea.row : active.row;
      const anchorCol = selectArea.active ? selectArea.col : active.col;
      cells.get(cellKey(active.row, active.col))?.classList.remove('sheet-cell-active', 'sheet-cell-editing');
      active = { row, col };
      editMode = false;
      tabAnchorCol = null;
      const nextCell = cells.get(cellKey(row, col));
      nextCell?.classList.add('sheet-cell-active');
      const inpExtend = nextCell ? getCellEditor(nextCell) : null;
      if (inpExtend) {
        const stored = data.get(row, col);
        inpExtend.value = stored !== undefined ? String(stored) : '';
      }
      selectArea.row = anchorRow;
      selectArea.col = anchorCol;
      selectArea.rowEnd = row;
      selectArea.colEnd = col;
      selectArea.active = anchorRow !== row || anchorCol !== col;
      syncSelectionHighlight();
      if (selectArea.active) enterRangeSelectionUi();
      else {
        exitRangeSelectionUi();
        focusActiveCell();
      }
      return;
    }

    exitRangeSelectionUi();

    dragging = true;
    dragMoved = false;
    dragPointerId = e.pointerId;
    dragAnchor = { row, col };
    tabAnchorCol = null;
    viewport.classList.add('sheet-viewport--dragging');
    viewport.setPointerCapture(e.pointerId);

    selectArea.active = false;
    selectArea.row = row;
    selectArea.col = col;
    selectArea.rowEnd = row;
    selectArea.colEnd = col;
    syncSelectionHighlight();

    hideSuggestions();
    persistActiveInput();
    editMode = false;
    cells.get(cellKey(active.row, active.col))?.classList.remove('sheet-cell-active', 'sheet-cell-editing');

    active = { row, col };
    const next = cells.get(cellKey(row, col));
    next?.classList.add('sheet-cell-active');
    const inp = next ? getCellEditor(next) : null;
    if (inp) {
      const stored = data.get(row, col);
      inp.value = stored !== undefined ? String(stored) : '';
    }

    // Second click of a double-click: enter edit mode here because
    // preventDefault() on pointerdown suppresses the dblclick event in most browsers.
    if (e.detail === 2 && isEditableCol(col) && !isGhostRow(row)) {
      enterEditMode();
    }
  });

  function computeAutoScrollDelta(clientX: number, clientY: number): { dx: number; dy: number } {
    const rect = viewport.getBoundingClientRect();
    const edge = 30;
    const maxSpeed = 24;
    let dx = 0;
    let dy = 0;
    if (clientX < rect.left + edge) {
      dx = -Math.min(maxSpeed, Math.ceil((rect.left + edge - clientX) / 2));
    } else if (clientX > rect.right - edge) {
      dx = Math.min(maxSpeed, Math.ceil((clientX - (rect.right - edge)) / 2));
    }
    if (clientY < rect.top + edge) {
      dy = -Math.min(maxSpeed, Math.ceil((rect.top + edge - clientY) / 2));
    } else if (clientY > rect.bottom - edge) {
      dy = Math.min(maxSpeed, Math.ceil((clientY - (rect.bottom - edge)) / 2));
    }
    return { dx, dy };
  }

  function stopAutoScroll(): void {
    if (autoScrollRaf !== null) {
      cancelAnimationFrame(autoScrollRaf);
      autoScrollRaf = null;
    }
  }

  function autoScrollTick(): void {
    autoScrollRaf = null;
    if (!dragging || !dragLastPointer) return;
    const { clientX, clientY } = dragLastPointer;
    const { dx, dy } = computeAutoScrollDelta(clientX, clientY);
    if (dx === 0 && dy === 0) return;
    viewport.scrollBy(dx, dy);
    const hit = cellFromPoint(clientX, clientY);
    if (hit) {
      if (hit.row !== dragAnchor.row || hit.col !== dragAnchor.col) dragMoved = true;
      dragPendingEnd = hit;
      scheduleDragPaint();
    }
    autoScrollRaf = requestAnimationFrame(autoScrollTick);
  }

  function ensureAutoScroll(clientX: number, clientY: number): void {
    dragLastPointer = { clientX, clientY };
    const { dx, dy } = computeAutoScrollDelta(clientX, clientY);
    if (dx === 0 && dy === 0) {
      stopAutoScroll();
      return;
    }
    if (autoScrollRaf === null) {
      autoScrollRaf = requestAnimationFrame(autoScrollTick);
    }
  }

  viewport.addEventListener('pointermove', (e) => {
    if (!dragging || e.pointerId !== dragPointerId) return;
    dragLastPointer = { clientX: e.clientX, clientY: e.clientY };
    const hit = cellFromPoint(e.clientX, e.clientY);
    if (hit) {
      if (hit.row !== dragAnchor.row || hit.col !== dragAnchor.col) dragMoved = true;
      dragPendingEnd = hit;
      scheduleDragPaint();
    }
    ensureAutoScroll(e.clientX, e.clientY);
  });

  viewport.addEventListener('pointerup', (e) => {
    if (e.pointerId !== dragPointerId) return;
    try {
      viewport.releasePointerCapture(e.pointerId);
    } catch {
      /* released */
    }
    stopAutoScroll();
    dragLastPointer = null;
    flushDragPaint();
    endPointerDrag(true);
  });

  viewport.addEventListener('pointercancel', (e) => {
    if (e.pointerId !== dragPointerId) return;
    try {
      viewport.releasePointerCapture(e.pointerId);
    } catch {
      /* released */
    }
    stopAutoScroll();
    dragLastPointer = null;
    flushDragPaint();
    endPointerDrag(false);
  });

  function isOccupied(r: number, c: number): boolean {
    const v = data.get(r, c);
    if (v === undefined) return false;
    if (typeof v === 'number') return true;
    return String(v).trim().length > 0;
  }

  function jumpCol(r: number, c: number, directionRight: boolean): number {
    const row = clampRow(r);
    const start = clampColVisible(c);
    const first = firstVisibleCol();
    const last = lastVisibleCol();
    if (first > last) return start;
    return jumpToEdge(
      start,
      directionRight ? last : first,
      directionRight ? 1 : -1,
      (col) => isVisibleCol(col) && isOccupied(row, col),
    );
  }

  function jumpRow(r: number, c: number, directionDown: boolean): number {
    const col = clampCol(c);
    return jumpToEdge(
      clampRow(r),
      directionDown ? dataRowCount : 1,
      directionDown ? 1 : -1,
      (row) => isOccupied(row, col),
    );
  }

  /** Step off a merge before moving, so arrows and Tab do not land on a covered cell. */
  function cellAfterStep(
    row: number,
    col: number,
    dRow: number,
    dCol: number,
  ): { row: number; col: number } {
    const origin = coveringSpan(row, col);
    let nextRow = row;
    let nextCol = col;
    if (origin) {
      if (dRow > 0) nextRow = origin.row + origin.rowSpan - 1;
      else if (dRow < 0) nextRow = origin.row;
      if (dCol > 0) nextCol = origin.col + origin.colSpan - 1;
      else if (dCol < 0) nextCol = origin.col;
    }
    if (dRow !== 0) nextRow = clampRow(nextRow + (dRow > 0 ? 1 : -1));
    if (dCol !== 0) nextCol = stepVisibleCol(nextCol, dCol > 0 ? 1 : -1);
    return { row: nextRow, col: nextCol };
  }

  function handleSheetKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target || !viewport.contains(target)) return;

    if (suggestionKeysConsumed(e)) return;

    if (!commentPreviewPop.hidden && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      hideCommentPreview();
      return;
    }

    const sheetModEarly = e.metaKey || e.ctrlKey;
    if (commentsEnabled && e.key === 'F2' && e.shiftKey && !sheetModEarly && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      openCommentEditor();
      return;
    }

    if (e.key === 'F2' && !e.shiftKey && !sheetModEarly && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      enterEditMode();
      return;
    }

    if (
      !editMode &&
      e.altKey &&
      e.key === 'ArrowDown' &&
      !sheetModEarly &&
      !e.shiftKey &&
      !selectArea.active &&
      isSelectColumn(columnAt(active.col)) &&
      !isReadOnlyCol(active.col) &&
      !isGhostRow(active.row)
    ) {
      e.preventDefault();
      e.stopPropagation();
      enterEditMode();
      openFullSuggestionList();
      return;
    }

    const hit = target.closest('.sheet-cell');
    const focusRowCol = hit
      ? {
          row: clampRow(Number((hit as HTMLElement).dataset.row)),
          col: clampCol(Number((hit as HTMLElement).dataset.col)),
        }
      : { row: active.row, col: active.col };

    const { row, col } = focusRowCol;
    const sheetMod = e.metaKey || e.ctrlKey;

    if (editMode) {
      // Escape / Tab / Enter fall through to shared handlers below. Plain arrow
      // keys commit and move to the adjacent cell (movement intent). Everything
      // else (Home/End, Cmd+A, Cmd+Z, typing, Backspace, Delete, modified
      // arrows) is left to the browser so the user can edit text naturally.
      if (
        (e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight') &&
        !sheetMod &&
        !e.shiftKey &&
        !e.altKey
      ) {
        e.preventDefault();
        hideSuggestions();
        tabAnchorCol = null;
        const dRow = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
        const dCol = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
        const next = cellAfterStep(row, col, dRow, dCol);
        setActive(next.row, next.col);
        return;
      }
      if (e.key !== 'Escape' && e.key !== 'Tab' && e.key !== 'Enter') {
        return;
      }
    }

    if (
      !editMode &&
      imageLayer !== null &&
      imageLayer.hasSelection() &&
      (e.key === 'Backspace' || e.key === 'Delete') &&
      !e.altKey &&
      !sheetMod
    ) {
      e.preventDefault();
      e.stopPropagation();
      imageLayer.removeSelected();
      return;
    }

    if (history && sheetMod && (e.key === 'z' || e.key === 'Z') && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) doRedo();
      else doUndo();
      return;
    }
    if (history && e.ctrlKey && !e.metaKey && (e.key === 'y' || e.key === 'Y') && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      doRedo();
      return;
    }

    // Navigation mode keeps focus on the viewport (a non-editable div). Browsers
    // do not fire copy/cut/paste for keyboard shortcuts on those elements.
    if (
      !editMode &&
      sheetMod &&
      !e.altKey &&
      !e.shiftKey &&
      (e.key === 'c' ||
        e.key === 'C' ||
        e.key === 'x' ||
        e.key === 'X' ||
        e.key === 'v' ||
        e.key === 'V')
    ) {
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        e.stopPropagation();
        writeClipboardText(buildCopyPlainText());
        setCopyMarqueeFromSelection();
        return;
      }
      if (e.key === 'x' || e.key === 'X') {
        e.preventDefault();
        e.stopPropagation();
        writeClipboardText(buildCopyPlainText());
        setCopyMarqueeFromSelection();
        clearWritableCellsInActiveRange();
        syncSelectionHighlight();
        return;
      }
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        e.stopPropagation();
        void readClipboardPayloadForPaste().then((payload) => {
          if (payload) applyPastedGrid(payload);
        });
        return;
      }
    }

    if (sheetMod && (e.key === 'a' || e.key === 'A') && !e.altKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      tabAnchorCol = null;
      if (dataRowCount > 0 && visibleColumnCount(columns) > 0) {
        persistActiveInput();
        selectArea.row = 1;
        selectArea.col = firstVisibleCol();
        selectArea.rowEnd = dataRowCount;
        selectArea.colEnd = lastVisibleCol();
        selectArea.active =
          dataRowCount > 1 || firstVisibleCol() !== lastVisibleCol();
        syncSelectionHighlight();
        if (selectArea.active) enterRangeSelectionUi();
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      imageLayer?.selectNone();
      hideSuggestions();
      barDirtyCoord = null;
      if (editMode) {
        const cellEl = cells.get(cellKey(active.row, active.col));
        const inp = cellEl ? getCellEditor(cellEl) : null;
        if (inp) {
          const stored = data.get(active.row, active.col);
          inp.value = stored !== undefined ? String(stored) : '';
        }
      }
      clearCopyMarquee();
      collapseRange();
      tabAnchorCol = null;
      enterNavigationMode();
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      hideSuggestions();
      if (tabAnchorCol === null) tabAnchorCol = active.col;
      const dir = e.shiftKey ? -1 : 1;
      const next = cellAfterStep(row, col, 0, dir);
      setActive(next.row, next.col);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      hideSuggestions();
      if (!e.shiftKey && !editMode && !selectArea.active && !sheetMod && !e.altKey) {
        if (activateLinkCell(row, col)) return;
      }
      const targetCol = clampColVisible(tabAnchorCol ?? col);
      tabAnchorCol = null;
      const dir = e.shiftKey ? -1 : 1;
      const next = cellAfterStep(row, targetCol, dir, 0);
      setActive(next.row, next.col);
      return;
    }

    if (e.key === 'Home' && !e.altKey && !e.shiftKey) {
      e.preventDefault();
      tabAnchorCol = null;
      setActive(sheetMod ? 1 : row, firstVisibleCol());
      return;
    }

    if (e.key === 'End' && !e.altKey && !e.shiftKey) {
      e.preventDefault();
      tabAnchorCol = null;
      setActive(sheetMod ? dataRowCount : row, lastVisibleCol());
      return;
    }

    if (e.key === 'PageDown' && !sheetMod && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      tabAnchorCol = null;
      const visibleRows = Math.max(1, Math.floor(viewport.clientHeight / defaultRowHeight));
      setActive(row + visibleRows, col);
      return;
    }

    if (e.key === 'PageUp' && !sheetMod && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      tabAnchorCol = null;
      const visibleRows = Math.max(1, Math.floor(viewport.clientHeight / defaultRowHeight));
      setActive(row - visibleRows, col);
      return;
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && selectArea.active) {
      e.preventDefault();
      const keys: string[] = [];
      doOnEverySelectedCell((cell) => {
        const r = clampRow(Number(cell.dataset.row));
        const c = clampCol(Number(cell.dataset.col));
        if (isEditableCol(c)) keys.push(cellKey(r, c));
      });
      const before = history && keys.length > 0 ? captureSnapshot(keys) : null;
      doOnEverySelectedCell((cell) => {
        const r = clampRow(Number(cell.dataset.row));
        const c = clampCol(Number(cell.dataset.col));
        if (!isEditableCol(c)) return;
        data.set(r, c, '');
        const contentEl = cell.querySelector<HTMLDivElement>('.sheet-cell-content')!;
        applyCellDisplay(contentEl, columnAt(c), '');
        getCellEditor(cell)!.value = '';
        syncCellChrome(r, c);
      });
      if (history && before) {
        const after = captureSnapshot(keys);
        pushHistoryIfChanged(before, after);
      }
      exitRangeSelectionUi();
      syncSelectionHighlight();
      return;
    }

    if (
      !editMode &&
      !sheetMod &&
      !e.altKey &&
      e.key.length === 1 &&
      isEditableCol(active.col) &&
      !isGhostRow(active.row)
    ) {
      e.preventDefault();
      if (selectArea.active) collapseRange();
      enterEditMode({ seed: e.key });
      return;
    }

    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      !selectArea.active &&
      !editMode &&
      isEditableCol(active.col) &&
      !isGhostRow(active.row)
    ) {
      e.preventDefault();
      const k = cellKey(active.row, active.col);
      const before = history ? captureSnapshot([k]) : null;
      data.set(active.row, active.col, '');
      const cellEl = cells.get(k);
      if (cellEl) {
        const contentEl = cellEl.querySelector<HTMLDivElement>('.sheet-cell-content');
        if (contentEl) applyCellDisplay(contentEl, columnAt(active.col), '');
        const inp = getCellEditor(cellEl);
        if (inp) inp.value = '';
        syncCellChrome(active.row, active.col);
      }
      if (history && before) {
        const after = captureSnapshot([k]);
        pushHistoryIfChanged(before, after);
      }
      focusActiveCell();
      return;
    }

    if (sheetMod && e.shiftKey) {
      if (
        e.key !== 'ArrowUp' &&
        e.key !== 'ArrowDown' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight'
      ) {
        return;
      }
      e.preventDefault();
      if (!selectArea.active) {
        const ar = row;
        const ac = col;
        selectArea.row = ar;
        selectArea.col = ac;
        selectArea.rowEnd = ar;
        selectArea.colEnd = ac;
      }
      selectArea.active = true;
      const cr = selectArea.rowEnd;
      const cc = selectArea.colEnd;
      if (e.key === 'ArrowUp') selectArea.rowEnd = jumpRow(cr, cc, false);
      else if (e.key === 'ArrowDown') selectArea.rowEnd = jumpRow(cr, cc, true);
      else if (e.key === 'ArrowLeft') selectArea.colEnd = jumpCol(cr, cc, false);
      else if (e.key === 'ArrowRight') selectArea.colEnd = jumpCol(cr, cc, true);

      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      const multi = minRow !== maxRow || minCol !== maxCol;

      if (!multi) {
        selectArea.active = false;
        selectArea.row = minRow;
        selectArea.col = minCol;
        selectArea.rowEnd = minRow;
        selectArea.colEnd = minCol;
        exitRangeSelectionUi();
        syncSelectionHighlight();
        focusActiveCell();
        return;
      }

      syncSelectionHighlight();
      enterRangeSelectionUi();
      return;
    }

    if (e.shiftKey) {
      if (
        e.key !== 'ArrowUp' &&
        e.key !== 'ArrowDown' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight'
      ) {
        return;
      }
      e.preventDefault();
      if (!selectArea.active) {
        const ar = row;
        const ac = col;
        selectArea.row = ar;
        selectArea.col = ac;
        selectArea.rowEnd = ar;
        selectArea.colEnd = ac;
      }
      selectArea.active = true;
      if (e.key === 'ArrowUp') selectArea.rowEnd = clampRow(selectArea.rowEnd - 1);
      else if (e.key === 'ArrowDown') selectArea.rowEnd = clampRow(selectArea.rowEnd + 1);
      else if (e.key === 'ArrowLeft') selectArea.colEnd = stepVisibleCol(selectArea.colEnd, -1);
      else if (e.key === 'ArrowRight') selectArea.colEnd = stepVisibleCol(selectArea.colEnd, 1);

      const minRow = Math.min(selectArea.row, selectArea.rowEnd);
      const maxRow = Math.max(selectArea.row, selectArea.rowEnd);
      const minCol = Math.min(selectArea.col, selectArea.colEnd);
      const maxCol = Math.max(selectArea.col, selectArea.colEnd);
      const multi = minRow !== maxRow || minCol !== maxCol;

      if (!multi) {
        selectArea.active = false;
        selectArea.row = minRow;
        selectArea.col = minCol;
        selectArea.rowEnd = minRow;
        selectArea.colEnd = minCol;
        exitRangeSelectionUi();
        syncSelectionHighlight();
        focusActiveCell();
        return;
      }

      syncSelectionHighlight();
      enterRangeSelectionUi();
      return;
    }

    if (sheetMod) {
      if (
        e.key !== 'ArrowUp' &&
        e.key !== 'ArrowDown' &&
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight'
      ) {
        return;
      }
      e.preventDefault();
      tabAnchorCol = null;
      let nr = row;
      let nc = col;
      if (e.key === 'ArrowUp') nr = jumpRow(row, col, false);
      else if (e.key === 'ArrowDown') nr = jumpRow(row, col, true);
      else if (e.key === 'ArrowLeft') nc = jumpCol(row, col, false);
      else if (e.key === 'ArrowRight') nc = jumpCol(row, col, true);
      setActive(nr, nc);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      tabAnchorCol = null;
      const next = cellAfterStep(row, col, -1, 0);
      setActive(next.row, next.col);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      tabAnchorCol = null;
      const next = cellAfterStep(row, col, 1, 0);
      setActive(next.row, next.col);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      tabAnchorCol = null;
      const next = cellAfterStep(row, col, 0, -1);
      setActive(next.row, next.col);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      tabAnchorCol = null;
      const next = cellAfterStep(row, col, 0, 1);
      setActive(next.row, next.col);
    }
  }

  viewport.addEventListener('keydown', handleSheetKeydown, true);

  viewport.addEventListener(
    'input',
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || !t.classList.contains('sheet-cell-input')) return;
      const cell = t.closest('.sheet-cell');
      if (!cell) return;
      const row = Number((cell as HTMLElement).dataset.row);
      const col = Number((cell as HTMLElement).dataset.col);
      if (!Number.isFinite(row) || !Number.isFinite(col)) return;
      editMode = true;
      persistError.clear(row, col);
    },
    true,
  );

  viewport.addEventListener('dblclick', (e) => {
    const target = e.target as HTMLElement;
    const hit = target.closest('.sheet-cell');
    if (!hit || !viewport.contains(hit)) return;
    const cell = hit as HTMLElement;
    const row = clampRow(Number(cell.dataset.row));
    const col = clampCol(Number(cell.dataset.col));
    if (!isEditableCol(col) || isGhostRow(row)) return;
    e.preventDefault();
    if (row !== active.row || col !== active.col) {
      setActive(row, col);
    }
    enterEditMode();
  });

  if (history) {
    history.subscribe(notifyHistoryChange);
  }

  if (config.initialSelection) {
    syncSelectionHighlight();
    focusActiveCell();
  }

  const scrollRestore = config.initialViewportScroll;
  if (scrollRestore) {
    const top = Number.isFinite(scrollRestore.scrollTop) ? scrollRestore.scrollTop : 0;
    const left = Number.isFinite(scrollRestore.scrollLeft) ? scrollRestore.scrollLeft : 0;
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const maxY = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
        const maxX = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        viewport.scrollTop = Math.max(0, Math.min(top, maxY));
        viewport.scrollLeft = Math.max(0, Math.min(left, maxX));
      });
    });
  } else if (config.initialSelection) {
    queueMicrotask(() => {
      cells.get(cellKey(active.row, active.col))?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  function findInSheet(options: { query: string; forward: boolean }): boolean {
    const q = options.query.trim();
    if (!q) return false;
    const total = dataRowCount * dataColumnCount;
    if (total < 1) return false;
    const needle = q.toLowerCase();
    const activeFlat = (active.row - 1) * dataColumnCount + (active.col - 1);
    for (let step = 1; step < total; step++) {
      const flat = options.forward
        ? (activeFlat + step) % total
        : ((activeFlat - step) % total + total) % total;
      const row = Math.floor(flat / dataColumnCount) + 1;
      const col = (flat % dataColumnCount) + 1;
      if (isHiddenCol(col) || isCoveredCell(row, col)) continue;
      const colDef = columnAt(col);
      const raw = data.get(row, col);
      const text = cellPlainTextForSearch(colDef, raw).toLowerCase();
      if (text.includes(needle)) {
        setActive(row, col);
        requestAnimationFrame(() => {
          cells.get(cellKey(row, col))?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
        return true;
      }
    }
    return false;
  }

  const handle: SpreadsheetMountHandle = {
    historyEnabled,
    commentsEnabled,
    openCommentEditor,
    mergeCellStyleOnSelection,
    mergeCellStyleOnEachTarget,
    everyTargetCellStyle,
    getCellStyleAt(row, col) {
      return data.getCellStyle?.(row, col);
    },
    colorsUsedInSheet,
    uniformTargetCellStyle,
    subscribeSelectionChange(cb) {
      selectionChangeListeners.add(cb);
      return () => selectionChangeListeners.delete(cb);
    },
    undo: () => doUndo(),
    redo: () => doRedo(),
    canUndo: () => history?.canUndo() ?? false,
    canRedo: () => history?.canRedo() ?? false,
    subscribeHistoryChange(cb) {
      historyChangeListeners.add(cb);
      return () => historyChangeListeners.delete(cb);
    },
    runHistoryBatch(fn) {
      if (history) history.runBatch(fn);
      else fn();
    },
    beginHistoryBatch() {
      history?.beginBatch();
    },
    endHistoryBatch() {
      history?.endBatch();
    },
    applyExternalValue,
    applyExternalStyle,
    mergeCells,
    unmergeCells,
    canMergeCells,
    canUnmergeCells,
    getCellSpans() {
      return currentSpans();
    },
    applyExternalSpans,
    getFloatingImages() {
      return data.getFloatingImages?.() ?? [];
    },
    insertFloatingImage(image) {
      return imageLayer?.insert(image.src, image.width, image.height) ?? false;
    },
    insertFloatingImageFile(file) {
      return imageLayer?.insertFile(file) ?? Promise.resolve(false);
    },
    applyExternalImages(images) {
      if (!data.setFloatingImages || !data.getFloatingImages) return false;
      const merged = mergeFloatingImages(data.getFloatingImages(), normalizeFloatingImages(images));
      data.setFloatingImages(merged);
      imageLayer?.sync();
      return true;
    },
    getCollabPresencePayload,
    setRemoteCollabPresence,
    showCellPersistError: (row, col, message) => persistError.show(row, col, message),
    replayClipboardPaste: applyPastedGrid,
    applyRemoteRowClear,
    findInSheet,
    getZoom,
    setZoom,
    subscribeZoomChange(cb) {
      zoomChangeListeners.add(cb);
      return () => zoomChangeListeners.delete(cb);
    },
    getActiveCellAddress() {
      const rect = currentSelectionRect();
      return rect.rowSpan > 1 || rect.colSpan > 1 ? cellRangeLabel(rect) : cellAddress(rect.row, rect.col);
    },
    focusValueBar() {
      valueBar?.focus();
    },
    disconnectLayout,
  };

  return handle;
}
